// app-ui/src/components/canvas/CanvasViewer.tsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactFlow, {
    Controls,
    ControlButton,
    Background,
    NodeChange,
    EdgeChange,
    Connection,
    addEdge,
    MiniMap,
    NodeTypes,
    BackgroundVariant,
    useNodesState,
    useEdgesState,
    Position,
    ConnectionLineType,
    Node,
    Edge,
    OnNodesChange,
    applyNodeChanges,
    EdgeProps,
    EdgeTypes,
    getSmoothStepPath,
    MarkerType,
    ReactFlowInstance
} from 'reactflow';
import 'reactflow/dist/style.css';
import apiClient from '../../../../../../shared/lib/apiClient';
import LlmService from '../../../../channels/chat/api/llmApi';
import RequirementsService from '../../api/requirementsApi';
import {
    generateManualRequirementUiid,
    getCurrentProjectIdFromLocalStorage,
} from '../../../../../../shared/lib/currentProjectSession';
import { 
    ProjectRequirementsResponse, 
    UserFlow, 
    BuildFeatureRequest, 
    BuildFeatureResponse
} from '@/shared/types/project';
import type { DockerBuildContract } from '../../../../channels/build_feature/types';
import { CustomNode, FilteredRequirementNodeData, TableRowData, ColumnDef } from '@/shared/types/canvas';
import FilteredRequirementNode from './FilteredRequirementNode';
import FlowNode, { 
    FlowNodeType, 
    FlowNodeData,
    childNodeTypeForParent,
    requirementTypeLabel,
    useFlowNodeHandlers,
    buildNodeActions,
    NodeActionsPopupState,
    nodeActionsPopupInitialState
} from './FlowNode';
import FlowDetailModal from './FlowDetailModal';
import DescriptionAttachmentUpload, {
    appendAttachmentSuffixToDescription,
} from './DescriptionAttachmentUpload';
import GenerateChildProgressModal, {
    type GenerateChildProgressStatus
} from './GenerateChildProgressModal';
import Toast from '../../../../../../shared/ui/Toast';
import BuildSummaryModal from '../../../../channels/build_feature/components/BuildSummaryModal';
import Modal from '../../../../../../shared/ui/Modal';
import VishCoderChatPanel from '../../../../channels/chat/components/VishCoderChatPanel';
import { useVishCoderChat } from '../../../../channels/chat/components/useVishCoderChat';
import { addChatMessage } from '../../../../channels/chat/components/sharedChatService';
import { useVishCoderActions } from '../../../../channels/build_feature/components/useVishCoderActions';
import BuildChannelHandler from '../../../../channels/build_feature/components/BuildChannelHandler';
import NodeActionsPopup, { NodeAction } from './NodeActionsPopup';
import {
    buildOutlineTreeFromCanvas,
    collectExpandableOutlineIds,
    collectOutlineBranchIds,
    collectExpandIdsForCanvasNode,
    type OutlineTreeNode,
} from './canvasOutlineTree';
import { useRequirementsCanvasOutlineOptional } from './requirementsCanvasOutlineContext';
import { createCommunicationManager as commManager } from '../../../../../../shared/lib/comm/gateway_ws/outbound';
import { getLatestBuild } from '../../../../../../shared/lib/buildStatusTracker';

/** Same `projectId` + `refreshTrigger` already built the graph (seed or GET) — avoids duplicate work when toggling UI or under Strict Mode. */
const requirementsHydratedKeys = new Set<string>();

function markRequirementsHydrated(projectId: string, refreshTrigger: number) {
    requirementsHydratedKeys.add(`${projectId}:${refreshTrigger}`);
}

function isRequirementsHydrated(projectId: string, refreshTrigger: number): boolean {
    return requirementsHydratedKeys.has(`${projectId}:${refreshTrigger}`);
}

// Constants for layout calculations
const BASE_ROW_HEIGHT = 40;
const NODE_HEADER_HEIGHT = 50;
const NODE_HORIZONTAL_SPACING = 250; // Horizontal spacing between sibling nodes
const NODE_VERTICAL_SPACING = 200;   // Vertical spacing between parent and children levels
const START_NODE_X = 0;               // X position for the start node (will be centered)
const START_NODE_Y = 50;              // Y position for the start node (at top)
const ESTIMATED_NODE_HEIGHT = 80;    // Estimated height of a flow node
const ESTIMATED_NODE_WIDTH = 250;    // Estimated width of a flow node
const DEFAULT_VISIBLE_ROWS = 3;      // Default number of rows to show when minimized
const MIN_READABLE_ZOOM = 0.55;     // Minimum zoom so node labels stay readable

const estimateNodeHeightForRows = (rowCount: number): number => {
    const safeRowCount = Math.max(1, rowCount);
    return NODE_HEADER_HEIGHT + safeRowCount * BASE_ROW_HEIGHT;
};

// Helper function to keep each hierarchy level on a dedicated horizontal band
const getLevelYPosition = (level: number): number => {
    // level = 0 (user flows) starts one spacing unit below the start node
    return START_NODE_Y + NODE_VERTICAL_SPACING * (level + 1);
};

// Helper function to format display names
const formatDisplayName = (key: string): string => {
    return key
        .replace(/_list$/, '') // Remove '_list' suffix
        .replace(/_/g, ' ')    // Replace underscores with spaces
        .split(' ')            // Split into words
        .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter
        .join(' ');            // Join back with spaces
};

// Helper function to determine field names for a data object
const determineFields = (item: any): { 
    displayField: string, 
    descriptionField: string | null,
    childrenField: string | null 
} => {
    // Common field names for display/title
    const possibleDisplayFields = ['name', 'title', 'requirement_text', 'description'];
    // Common field names for description
    const possibleDescFields = ['description', 'desc', 'expected_result', 'details'];
    // Field names that might contain child items (usually ending with _list)
    const possibleChildrenFields = Object.keys(item || {}).filter(key => 
        key.endsWith('_list') && Array.isArray(item[key])
    );

    // Find the first matching field that exists in the item
    const displayField = possibleDisplayFields.find(field => item && item[field]) || 
                        Object.keys(item || {}).find(key => typeof item[key] === 'string') || 
                        'id';

    // Don't use the same field for both display and description
    const descriptionField = possibleDescFields
        .filter(field => field !== displayField)
        .find(field => item && item[field]) || null;

    // The first field ending with _list that contains an array
    const childrenField = possibleChildrenFields.length > 0 ? possibleChildrenFields[0] : null;

    return { displayField, descriptionField, childrenField };
};

/** BFS from anchorId following all edges source→target. Returns the full descendant set including self. */
function collectDescendants(anchorId: string, edges: Edge[]): Set<string> {
    const bySource = new Map<string, string[]>();
    for (const e of edges) {
        if (!bySource.has(e.source)) bySource.set(e.source, []);
        bySource.get(e.source)!.push(e.target);
    }
    const set = new Set<string>();
    const q = [anchorId];
    while (q.length) {
        const id = q.shift()!;
        if (set.has(id)) continue;
        set.add(id);
        for (const c of bySource.get(id) || []) q.push(c);
    }
    return set;
}

/** BFS from nodeId following all edges target→source (upward). Returns ancestor set including self. */
function collectAncestors(nodeId: string, edges: Edge[]): Set<string> {
    const byTarget = new Map<string, string[]>();
    for (const e of edges) {
        if (!byTarget.has(e.target)) byTarget.set(e.target, []);
        byTarget.get(e.target)!.push(e.source);
    }
    const set = new Set<string>();
    const q = [nodeId];
    while (q.length) {
        const id = q.shift()!;
        if (set.has(id)) continue;
        set.add(id);
        for (const p of byTarget.get(id) || []) q.push(p);
    }
    return set;
}

/** Same algorithm as branch-focus relayout: wide leaf slots so the tree is readable on first paint. */
function computeTreeLayoutPositions(
    nodes: Node[],
    edges: Edge[],
    visibleIds: Set<string>
): Map<string, { x: number; y: number }> {
    const LEAF_SLOT = 320;
    const SIBLING_GAP = 40;
    const LEVEL_GAP = 240;

    const childrenOf = new Map<string, string[]>();
    const hasParent = new Set<string>();
    for (const e of edges) {
        if (!visibleIds.has(e.source) || !visibleIds.has(e.target)) continue;
        if (!childrenOf.has(e.source)) childrenOf.set(e.source, []);
        childrenOf.get(e.source)!.push(e.target);
        hasParent.add(e.target);
    }

    const snoLookup = new Map<string, number>();
    for (const n of nodes) {
        const sno = (n.data as { sno?: number })?.sno;
        if (typeof sno === 'number') snoLookup.set(n.id, sno);
    }
    for (const [, children] of childrenOf) {
        children.sort((a, b) => (snoLookup.get(a) ?? 0) - (snoLookup.get(b) ?? 0));
    }

    const roots = [...visibleIds].filter((id) => !hasParent.has(id));

    const posMap = new Map<string, { x: number; y: number }>();

    function layout(id: string, xStart: number, y: number): number {
        const ch = childrenOf.get(id);
        if (!ch || ch.length === 0) {
            posMap.set(id, { x: xStart, y });
            return LEAF_SLOT;
        }
        let cx = xStart;
        let totalW = 0;
        for (let i = 0; i < ch.length; i++) {
            if (i > 0) {
                cx += SIBLING_GAP;
                totalW += SIBLING_GAP;
            }
            const w = layout(ch[i], cx, y + LEVEL_GAP);
            cx += w;
            totalW += w;
        }
        posMap.set(id, { x: xStart + totalW / 2 - LEAF_SLOT / 2, y });
        return totalW;
    }

    let xOff = 0;
    for (const r of roots) {
        const w = layout(r, xOff, START_NODE_Y);
        xOff += w + LEAF_SLOT;
    }

    return posMap;
}

interface CanvasViewerProps {
    projectId: string | null;
    refreshTrigger: number;
    onToggleSidebar?: () => void;
    showCanvas?: boolean;
    isBottomPanelOpen?: boolean;
    isRightPanelOpen?: boolean;
    onToggleBottomPanel?: () => void;
    onToggleRightPanel?: () => void;
    /** When false, user has read-only access (no add/delete/edit requirements). */
    canEdit?: boolean;
    /** Deep link: open this node and optionally highlight a comment (e.g. from notification click). */
    deepLinkOpen?: { targetType: string; targetUiid: string; commentId?: string } | null;
    /** Called after the deep-link node has been opened so parent can clear deepLinkOpen. */
    onDeepLinkHandled?: () => void;
    /**
     * When the parent already fetched `/requirements/:id`, pass the payload once so the canvas
     * hydrates without a duplicate GET. Parent should clear after `onInitialRequirementsConsumed`.
     */
    initialRequirements?: ProjectRequirementsResponse | null;
    onInitialRequirementsConsumed?: () => void;
    /**
     * When false, this instance does not publish the sidebar outline (e.g. a hidden canvas sibling is mounted).
     */
    outlineBridgeActive?: boolean;
}

// Custom edge component to handle errors with missing handles
const CustomSmoothStepEdge: React.FC<EdgeProps> = (props) => {
    // Just render a basic smooth step edge
    const { id, source, target, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, style } = props;
    
    // If we don't have all the required coordinates, return null
    if (sourceX === undefined || sourceY === undefined || targetX === undefined || targetY === undefined) {
        console.warn(`Cannot render edge ${id} due to missing coordinates`);
        return null;
    }
    
    // Get path for the edge
    const [edgePath, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition: sourcePosition || Position.Right,
        targetX,
        targetY,
        targetPosition: targetPosition || Position.Left,
    });
    
    return (
        <path
            id={id}
            style={{
                stroke: '#10b981',
                strokeWidth: 2,
                strokeDasharray: '5,5',
                ...style, // Allow style prop to override defaults
            }}
            className="react-flow__edge-path"
            d={edgePath}
            markerEnd={MarkerType.ArrowClosed}
        />
    );
};

// Define the custom edge types
const edgeTypes: EdgeTypes = {
    smoothstep: CustomSmoothStepEdge,
};

const targetTypeToNodeType: Record<string, string> = {
    user_flow: 'userFlowNode',
    high_level_requirement: 'highLevelRequirementNode',
    low_level_requirement: 'lowLevelRequirementNode',
    test_case: 'testCaseNode'
};

const targetTypeToFlowNodeType: Record<string, FlowNodeType> = {
    user_flow: 'userFlow',
    high_level_requirement: 'highLevelRequirement',
    low_level_requirement: 'lowLevelRequirement',
    test_case: 'testCase'
};

const FLOW_NODE_TYPES = ['userFlowNode', 'highLevelRequirementNode', 'lowLevelRequirementNode', 'testCaseNode'] as const;

function getChildGenerationCompletionMessage(
    source: 'flow' | 'table',
    flowNodeType?: string,
    tableNodeId?: string
): string {
    if (source === 'flow' && flowNodeType) {
        switch (flowNodeType) {
            case 'userFlowNode':
                return 'High level requirements created';
            case 'highLevelRequirementNode':
                return 'Low level requirements created';
            case 'lowLevelRequirementNode':
                return 'Test cases created';
            default:
                return 'Child requirements created';
        }
    }
    if (tableNodeId) {
        if (tableNodeId.startsWith('userflow_')) return 'High level requirements created';
        if (tableNodeId.startsWith('highlevelrequirement_')) return 'Low level requirements created';
        if (tableNodeId.startsWith('lowlevelrequirement_')) return 'Test cases created';
    }
    return 'Child requirements created';
}

function flowNodeDataFromCanvasNode(node: Node): FlowNodeData | null {
    const t = node.type;
    if (!t || !FLOW_NODE_TYPES.includes(t as (typeof FLOW_NODE_TYPES)[number])) {
        return null;
    }
    const d = node.data as Record<string, unknown>;
    const nodeType: FlowNodeType =
        t === 'userFlowNode'
            ? 'userFlow'
            : t === 'highLevelRequirementNode'
              ? 'highLevelRequirement'
              : t === 'lowLevelRequirementNode'
                ? 'lowLevelRequirement'
                : 'testCase';
    return {
        name: (d.name as string) || '',
        description: (d.description as string) || '',
        sno: (d.sno as number) || 0,
        uiid: (d.uiid as string) || (d.id as string) || '',
        id: node.id,
        originalData: d.originalData,
        nodeType: (d.nodeType as FlowNodeType) || nodeType
    };
}

const CanvasViewer: React.FC<CanvasViewerProps> = ({ 
    projectId, 
    refreshTrigger,
    onToggleSidebar, 
    showCanvas = true,
    isBottomPanelOpen = false,
    isRightPanelOpen = false,
    onToggleBottomPanel,
    onToggleRightPanel,
    canEdit = true,
    deepLinkOpen = null,
    onDeepLinkHandled,
    initialRequirements = null,
    onInitialRequirementsConsumed,
    outlineBridgeActive = true
}) => {
    // Communication Protocol: Uses centralized commManager for all WebSocket communication
    // - Session threads are managed automatically based on Low Level Requirement ID
    // - All payloads use standardized 'body' structure (contract, messages, statusDetails)
    // - Response validation and parsing handled by commManager
    const [nodes, setNodes, onNodesChange] = useNodesState<FilteredRequirementNodeData & { actions: any }>([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [userFlows, setUserFlows] = useState<UserFlow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    /** Shown under “Loading project” while the canvas data pipeline runs */
    const [loadStage, setLoadStage] = useState<'fetching' | 'building' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [, setGenerateLoading] = useState<boolean>(false);

    const [generateProgressOpen, setGenerateProgressOpen] = useState(false);
    const [generateProgressStatus, setGenerateProgressStatus] =
        useState<GenerateChildProgressStatus>('running');
    const [generateProgressLines, setGenerateProgressLines] = useState<string[]>([]);
    const [generateProgressContextTitle, setGenerateProgressContextTitle] = useState<string>('');
    const [generateProgressError, setGenerateProgressError] = useState<string | null>(null);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [focusedSubtreeIds, setFocusedSubtreeIds] = useState<Set<string> | null>(null);
    const originalPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
    const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    /** Latest `fitAllNodes` — Home outline control calls this after focusing Start (same as the Fit control button). */
    const fitAllNodesRef = useRef<(() => void) | null>(null);

    const flowModalSyncRef = useRef<{ isOpen: boolean; uiid: string | null }>({
        isOpen: false,
        uiid: null
    });
    const generateProgressStatusRef = useRef<GenerateChildProgressStatus>('running');
    const completionToastPendingRef = useRef<string | null>(null);
    const autoCloseProgressModalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const toastClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Note: refreshTrigger is received as a prop, not managed as state
    
    // Track which tables are minimized (initially all tables are minimized)
    const [minimizedTables, setMinimizedTables] = useState<Record<string, boolean>>({});
    
    // Track selected node for highlighting upstream/downstream
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [highlightedNodeIds, setHighlightedNodeIds] = useState<Set<string>>(new Set());
    const [highlightedEdgeIds, setHighlightedEdgeIds] = useState<Set<string>>(new Set());
    
    // Panel states - use props if provided, otherwise use internal state
    const bottomPanelOpen = isBottomPanelOpen;
    const rightPanelOpen = isRightPanelOpen;
    const [devMode, setDevMode] = useState<boolean>(false);
    
    // Chat hook for getting agent socket
    const { getAgentSocket, agentConnected } = useVishCoderChat('requirements');
    
    // Memoize agent socket to avoid calling getAgentSocket() on every render
    const agentSocket = useMemo(() => {
        try {
            return getAgentSocket() || null;
        } catch (error) {
            console.warn('Failed to get agent socket:', error);
            return null;
        }
    }, [getAgentSocket, agentConnected]);
    
    // Ref to store setContractPopups from BuildChannelHandler (use ref to avoid stale closures)
    const setContractPopupsRef = useRef<React.Dispatch<React.SetStateAction<Set<string>>> | null>(null);
    
    // Function to open contract modal (replaces wsManagerMethods.openContractModal)
    const openContractModal = useCallback((contract: DockerBuildContract) => {
        const contractMessage = {
            id: `contract-${Date.now()}`,
            type: 'assistant' as const,
            content: `📄 Contract: Generated Successfully`,
            timestamp: new Date(),
            isContract: true,
            contractData: contract,
            page: 'requirements' as const
        };
        addChatMessage(contractMessage);
        // Open the contract popup directly
        if (setContractPopupsRef.current) {
            setContractPopupsRef.current(prev => new Set([contractMessage.id]));
        } else {
            console.warn('setContractPopups not available yet, contract modal may not open');
        }
    }, []);
    
    // Terminal logs state
    const [terminalLogs, setTerminalLogs] = useState<string[]>([]);
    const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);
    const [shouldAutoFit, setShouldAutoFit] = useState<boolean>(false);
    
    // Ref to track if a fetch is in progress to prevent duplicate calls
    const isFetchingRef = useRef<boolean>(false);
    
    const addTerminalLog = useCallback((message: string) => {
        const timestamp = new Date().toLocaleTimeString();
        setTerminalLogs(prev => [...prev, `[${timestamp}] ${message}`]);
    }, []);
    
// Build Summary modal state
    const [isBuildSummaryModalOpen, setIsBuildSummaryModalOpen] = useState<boolean>(false);
    const [buildSummaryData, setBuildSummaryData] = useState<{
        lowLevelRequirementId: string;
        status: string;
        branchLink: string;
        prLink: string;
        documentLinks: string[];
        keyMetrics: string;
        dashboardLinks: string[];
        alerts: string;
        logs: string;
        productManager: string;
        devManager: string;
    } | null>(null);
    
    // User Flow Detail modal state
    const [selectedFlowNode, setSelectedFlowNode] = useState<FlowNodeData | null>(null);
    const [isFlowModalOpen, setIsFlowModalOpen] = useState<boolean>(false);
    const [deepLinkCommentId, setDeepLinkCommentId] = useState<string | null>(null);

    const [outlineExpandedIds, setOutlineExpandedIds] = useState<Set<string>>(() => new Set());
    const [outlineSelectedId, setOutlineSelectedId] = useState<string | null>(null);
    const [outlineFlashNodeId, setOutlineFlashNodeId] = useState<string | null>(null);
    const [outlineTestCaseHighlightUiid, setOutlineTestCaseHighlightUiid] = useState<string | null>(null);
    const outlineHydrateRef = useRef<{ expanded?: string[]; selected?: string | null } | null>(null);
    const skipNextOutlinePersistRef = useRef(false);
    const outlineFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        flowModalSyncRef.current = {
            isOpen: isFlowModalOpen,
            uiid: selectedFlowNode?.uiid ?? null
        };
    }, [isFlowModalOpen, selectedFlowNode]);

    useEffect(() => {
        generateProgressStatusRef.current = generateProgressStatus;
    }, [generateProgressStatus]);

    useEffect(() => {
        return () => {
            if (autoCloseProgressModalTimerRef.current) {
                clearTimeout(autoCloseProgressModalTimerRef.current);
            }
            if (toastClearTimerRef.current) {
                clearTimeout(toastClearTimerRef.current);
            }
            if (clickTimerRef.current) {
                clearTimeout(clickTimerRef.current);
            }
            if (outlineFlashTimerRef.current) {
                clearTimeout(outlineFlashTimerRef.current);
            }
        };
    }, []);
    
    // Add Requirement modal state
    const [isAddRequirementModalOpen, setIsAddRequirementModalOpen] = useState<boolean>(false);
    const [addRequirementParentUiid, setAddRequirementParentUiid] = useState<string | null>(null);
    const [addRequirementType, setAddRequirementType] = useState<FlowNodeType | null>(null);
    const [addRequirementIntent, setAddRequirementIntent] = useState<'sibling' | 'child'>('sibling');
    const [newRequirementName, setNewRequirementName] = useState<string>('');
    const [newRequirementDescription, setNewRequirementDescription] = useState<string>('');
    
    // Inline editing state
    const [editingField, setEditingField] = useState<string | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    
    
    // Process and beautify Docker logs
    const processDockerLogs = useCallback((rawLogs: string) => {
        const lines = rawLogs.split('\n');
        const processedLines: string[] = [];
        const now = Date.now();
        const hide_logs = [
            'health',
            'Health Check:',
            ' GET /'
        ]
        
        for (const line of lines) {
            // Skip empty lines
            if (!line.trim()) continue;
            
            // Check if line contains any text we want to hide
            const shouldHide = hide_logs.some(hideText => line.includes(hideText));
            if (shouldHide) {
                continue; // Skip this line completely
            }
            
            // Beautify ERROR logs with HTML
            if (line.includes(' - ERROR - ')) {
                const errorLine = line.replace(
                    /(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}),\d+ - ERROR - (.+)/,
                    '<div class="docker-log-line docker-log-error">❌ $2 <span class="docker-log-separator">|</span> <span class="docker-log-timestamp">$1</span></div>'
                );
                processedLines.push(errorLine);
                continue;
            }
            
            // Beautify WARNING logs with HTML
            if (line.includes(' - WARNING - ')) {
                const warningLine = line.replace(
                    /(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}),\d+ - WARNING - (.+)/,
                    '<div class="docker-log-line docker-log-warning">⚠️ $2 <span class="docker-log-separator">|</span> <span class="docker-log-timestamp">$1</span></div>'
                );
                processedLines.push(warningLine);
                continue;
            }
            
            // For any other lines, just add them as-is with basic styling
            processedLines.push(`<div class="docker-log-line">${line}</div>`);
        }
        

        
        return processedLines.join('');
    }, []);
    
    // Limit logs to last 200 lines
    const limitLogsToLastNLines = useCallback((logs: string, maxLines: number = 200) => {
        const lines = logs.split('\n');
        if (lines.length <= maxLines) {
            return logs;
        }
        
        // Keep only the last maxLines lines
        const limitedLines = lines.slice(-maxLines);
        return limitedLines.join('\n');
    }, []);
    
    
    // Chat scroll reference
    const chatMessagesEndRef = useRef<HTMLDivElement>(null);
    
    // Node actions popup state
    const [nodeActionsPopup, setNodeActionsPopup] = useState<NodeActionsPopupState>(nodeActionsPopupInitialState);
    
    const {
        handleNodeClick,
        handleNodeDoubleClick,
        handleFlowNodeDoubleClick,
        handleFlowSave: applyLocalFlowSave,
        handlePaneClickWithHighlight
    } = useFlowNodeHandlers({
        nodes,
        edges,
        setNodes,
        setSelectedNodeId,
        setHighlightedNodeIds,
        setHighlightedEdgeIds,
        setSelectedFlowNode,
        setIsFlowModalOpen,
        selectedFlowNode,
        setNodeActionsPopup
    });

    const handleFlowNodeDoubleClickRef = useRef(handleFlowNodeDoubleClick);
    handleFlowNodeDoubleClickRef.current = handleFlowNodeDoubleClick;

    /** Tree layout for the visible subtree (same geometry as full-graph default after closing branch focus). */
    const relayoutVisibleNodes = useCallback((visibleIds: Set<string>, saveOriginals = true) => {
        setNodes((nds) => {
            const posMap = computeTreeLayoutPositions(nds, edges, visibleIds);

            if (saveOriginals && originalPositionsRef.current.size === 0) {
                for (const n of nds) {
                    originalPositionsRef.current.set(n.id, { ...n.position });
                }
            }

            return nds.map((n) => {
                const p = posMap.get(n.id);
                return p ? { ...n, position: p } : n;
            });
        });
    }, [edges, setNodes]);

    const collapseFocusSubtree = useCallback((anchorNodeId: string) => {
        const descendants = collectDescendants(anchorNodeId, edges);
        const ancestors = collectAncestors(anchorNodeId, edges);
        const visible = new Set([...descendants, ...ancestors]);
        setFocusedSubtreeIds(visible);
        relayoutVisibleNodes(visible);
    }, [edges, relayoutVisibleNodes]);

    const clearBranchFocus = useCallback(() => {
        setFocusedSubtreeIds(null);
        originalPositionsRef.current.clear();
        const allIds = new Set(nodes.map(n => n.id));
        relayoutVisibleNodes(allIds, false);
    }, [nodes, relayoutVisibleNodes]);

    const outlineTree = useMemo(() => buildOutlineTreeFromCanvas(nodes, edges), [nodes, edges]);

    useEffect(() => {
        skipNextOutlinePersistRef.current = true;
        outlineHydrateRef.current = null;
        if (!projectId) {
            setOutlineExpandedIds(new Set());
            setOutlineSelectedId(null);
            return;
        }
        try {
            const rawE = localStorage.getItem(`vishmaker_outline_expanded_${projectId}`);
            const rawS = localStorage.getItem(`vishmaker_outline_selected_${projectId}`);
            outlineHydrateRef.current = {
                expanded: rawE ? (JSON.parse(rawE) as string[]) : undefined,
                selected: rawS || null,
            };
        } catch {
            outlineHydrateRef.current = {};
        }
        setOutlineExpandedIds(new Set());
        setOutlineSelectedId(null);
    }, [projectId]);

    useEffect(() => {
        const validExpand = collectExpandableOutlineIds(outlineTree);
        const allIds = collectOutlineBranchIds(outlineTree);
        const h = outlineHydrateRef.current;
        if (outlineTree.length > 0 && h) {
            if (h.expanded?.length) {
                setOutlineExpandedIds(new Set(h.expanded.filter((id) => validExpand.has(id))));
            }
            if (h.selected && allIds.has(h.selected)) {
                setOutlineSelectedId(h.selected);
            }
            outlineHydrateRef.current = null;
            return;
        }
        if (outlineTree.length === 0) return;
        setOutlineExpandedIds((prev) => {
            const next = new Set([...prev].filter((id) => validExpand.has(id)));
            if (next.size === prev.size && [...prev].every((id) => next.has(id))) return prev;
            return next;
        });
        setOutlineSelectedId((prev) => (prev && !allIds.has(prev) ? null : prev));
    }, [outlineTree]);

    useEffect(() => {
        if (!projectId) return;
        if (skipNextOutlinePersistRef.current) {
            skipNextOutlinePersistRef.current = false;
            return;
        }
        try {
            localStorage.setItem(`vishmaker_outline_expanded_${projectId}`, JSON.stringify([...outlineExpandedIds]));
            if (outlineSelectedId) {
                localStorage.setItem(`vishmaker_outline_selected_${projectId}`, outlineSelectedId);
            } else {
                localStorage.removeItem(`vishmaker_outline_selected_${projectId}`);
            }
        } catch {
            /* ignore */
        }
    }, [projectId, outlineExpandedIds, outlineSelectedId]);

    const handleOutlineFocusNode = useCallback(
        (outlineNode: OutlineTreeNode) => {
            const focusId = outlineNode.focusNodeId;
            const target = nodes.find((n) => n.id === focusId);
            if (!target) return;

            // Match canvas single-click: show this requirement’s branch (subtree) on the canvas.
            collapseFocusSubtree(focusId);

            setOutlineExpandedIds((prev) => {
                const next = new Set(prev);
                for (const id of collectExpandIdsForCanvasNode(focusId, edges)) {
                    next.add(id);
                }
                if (outlineNode.kind === 'test_case') {
                    next.add(focusId);
                }
                return next;
            });

            setOutlineSelectedId(outlineNode.id);

            const fakeEv = { stopPropagation: () => {} } as unknown as React.MouseEvent;
            handleNodeClick(fakeEv, target);

            if (outlineNode.kind === 'test_case' && outlineNode.testCaseUiid) {
                const d = target.data as unknown as FlowNodeData;
                setSelectedFlowNode({
                    name: d.name || '',
                    description: d.description || '',
                    sno: d.sno || 0,
                    uiid: d.uiid || '',
                    id: target.id,
                    originalData: d.originalData,
                    nodeType: 'lowLevelRequirement',
                });
                setIsFlowModalOpen(true);
                setOutlineTestCaseHighlightUiid(outlineNode.testCaseUiid);
            } else {
                setOutlineTestCaseHighlightUiid(null);
            }

            if (outlineFlashTimerRef.current) {
                clearTimeout(outlineFlashTimerRef.current);
            }
            setOutlineFlashNodeId(focusId);
            outlineFlashTimerRef.current = setTimeout(() => {
                setOutlineFlashNodeId(null);
                outlineFlashTimerRef.current = null;
            }, 2200);

            // Viewport: rely on the same `focusedSubtreeIds` effect as canvas branch clicks — fit the
            // full visible subtree (ancestors + descendants), not a single node.
        },
        [nodes, edges, collapseFocusSubtree, handleNodeClick]
    );

    const handleCanvasHome = useCallback(() => {
        const start = nodes.find((n) => n.id === 'start-node');
        if (!start) return;
        collapseFocusSubtree('start-node');
        const fakeEv = { stopPropagation: () => {} } as unknown as React.MouseEvent;
        handleNodeClick(fakeEv, start);
        // Same action as the React Flow “Fit entire canvas” control after branch focus updates.
        window.setTimeout(() => {
            fitAllNodesRef.current?.();
        }, 120);
    }, [nodes, collapseFocusSubtree, handleNodeClick]);

    /** Publish outline to the left sidebar (ProjectHomePage) when the bridge provider is present. */
    const outlineCtx = useRequirementsCanvasOutlineOptional();
    const setOutlinePanelFn = outlineCtx?.setOutlinePanel;

    useEffect(() => {
        if (!setOutlinePanelFn || !outlineBridgeActive) return;
        setOutlinePanelFn({
            tree: outlineTree,
            expandedIds: outlineExpandedIds,
            setExpandedIds: setOutlineExpandedIds,
            selectedId: outlineSelectedId,
            onFocusNode: handleOutlineFocusNode,
            onGoHome: handleCanvasHome,
        });
        return () => setOutlinePanelFn(null);
    }, [
        setOutlinePanelFn,
        outlineBridgeActive,
        outlineTree,
        outlineExpandedIds,
        outlineSelectedId,
        handleOutlineFocusNode,
        handleCanvasHome,
    ]);

    const FLOW_NODE_TYPE_SET = new Set(['userFlowNode', 'highLevelRequirementNode', 'lowLevelRequirementNode']);

    /** Single-click on a flow node focuses its branch (delayed to allow double-click to cancel). */
    const handleNodeClickWithFocus = useCallback((_event: React.MouseEvent, node: Node) => {
        if (!node.type || !FLOW_NODE_TYPE_SET.has(node.type)) return;
        if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
        clickTimerRef.current = setTimeout(() => {
            clickTimerRef.current = null;
            if (node.id === 'start-node') {
                clearBranchFocus();
                return;
            }
            collapseFocusSubtree(node.id);
        }, 250);
    }, [clearBranchFocus, collapseFocusSubtree]);

    /** Pane click clears branch focus and node highlight */
    const handlePaneClick = useCallback(() => {
        handlePaneClickWithHighlight();
        clearBranchFocus();
    }, [handlePaneClickWithHighlight, clearBranchFocus]);

    /** Double-click cancels any pending single-click timer, then opens the detail modal. */
    const handleNodeDoubleClickCancelTimer = useCallback((event: React.MouseEvent, node: Node) => {
        if (clickTimerRef.current) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
        }
        handleNodeDoubleClick(event, node);
    }, [handleNodeDoubleClick]);

    // Stable map for React Flow — handlers read from ref so this object identity stays fixed
    const nodeTypes: NodeTypes = useMemo(
        () => ({
            filteredRequirementNode: FilteredRequirementNode,
            userFlowNode: (props: any) => (
                <FlowNode
                    {...props}
                    nodeType="userFlow"
                    onNodeDoubleClick={(e, n) => handleFlowNodeDoubleClickRef.current(e, n)}
                />
            ),
            highLevelRequirementNode: (props: any) => (
                <FlowNode
                    {...props}
                    nodeType="highLevelRequirement"
                    onNodeDoubleClick={(e, n) => handleFlowNodeDoubleClickRef.current(e, n)}
                />
            ),
            lowLevelRequirementNode: (props: any) => (
                <FlowNode
                    {...props}
                    nodeType="lowLevelRequirement"
                    onNodeDoubleClick={(e, n) => handleFlowNodeDoubleClickRef.current(e, n)}
                />
            ),
        }),
        []
    );

    // Create a stable actions object that doesn't change on every render
    // This prevents React Flow from detecting changes and re-rendering nodes
    // Note: onGenerateChildReq will be added after handleGenerateRequirements is defined
    const nodeActionsRef = useRef({} as any);

    /**
     * Generic function to create a table node without specific table-type logic
     */
    const createGenericTable = (
        items: any[],                 // Array of data items for the rows
        nodeId: string,               // Unique ID for the node
        title: string,                // Table title
        fieldConfig: {                // Configuration for fields to use
            displayField: string,
            descriptionField: string | null
        },
        extraColumns: ColumnDef[] = [] // Any additional columns
    ): CustomNode => {
        // Common columns that appear in all tables
        const baseColumns: ColumnDef[] = [
            { key: 'sno', label: '#', order: 0, editable: false, width: 'w-[50px]' },
            { key: 'name', label: 'Name', order: 1, editable: true, width: 'w-[250px]' },
            { key: 'desc', label: 'Description', order: 2, editable: true, width: 'w-[450px]' },
            ...extraColumns
        ];

        // Create table rows from the items
        const tableRows: TableRowData[] = items.map((item, index) => {
            // Use the existing UIID from the database
            const sno = index + 1; // Ensure sno is always a number starting from 1
            // Extract UIID from the database record or create a placeholder
            const rowUiid = item.uiid || `placeholder-${nodeId}-${sno}`;
            
            // Ensure we capture the sno from the database if it exists 
            const dbSno = typeof item.sno === 'number' ? item.sno : sno;
            
            return {
                id: rowUiid, // Use the UIID as the row ID
                sno: dbSno, // Use database sno or our calculated one
                name: item[fieldConfig.displayField] || 'Unnamed Item',
                desc: fieldConfig.descriptionField && item[fieldConfig.descriptionField] 
                    ? item[fieldConfig.descriptionField] 
                    : '',
                originalId: item.id,
                uiid: rowUiid, // Store UIID separately in case we need it
                originalData: item // Store the original data for reference
            };
        });
        
        // Sort the rows by serial number
        tableRows.sort((a, b) => a.sno - b.sno);
        
        // By default, tables start minimized showing only DEFAULT_VISIBLE_ROWS
        const isMinimized = true;
        const visibleRows = tableRows.length > DEFAULT_VISIBLE_ROWS ? 
            tableRows.slice(0, DEFAULT_VISIBLE_ROWS) : 
            tableRows;

        console.log(`Creating table ${nodeId} with ${visibleRows.length} visible rows out of ${tableRows.length} total`);
        
        // Note: Initial minimized state is set in the node data itself (isMinimized: true)
        // No need to update minimizedTables state during render as that causes React error #185
        
        // Determine if this is a test case table or LLR table for context menu customization
        const isTestCase = nodeId.startsWith('testcase_');
        const isLlr = nodeId.startsWith('lowlevelrequirement_');
        
        // Create the node with the organized data
        return {
            id: nodeId,
            type: 'filteredRequirementNode',
            data: {
                title: title,
                componentId: nodeId.split('_').slice(0, 2).join('_'), // Use the nodeId as componentId for LLM processing
                columns: baseColumns,
                rows: visibleRows,
                allRows: tableRows,
                isMinimized: isMinimized,
                isTestCase: isTestCase, // Pass flag to determine table type
                isLlr: isLlr, // Pass flag to determine table type
                connectedRowUiids: [], // Will be populated with UIIDs of rows that have connections
                actions: nodeActionsRef.current, // Use stable actions object to prevent re-renders
                projectId: projectId // Add projectId to node data
            },
            position: { x: 0, y: 0 }, // Position will be calculated later
            style: {
                minHeight: `${estimateNodeHeightForRows(visibleRows.length)}px`
            }
        };
    };

    /**
     * Function to recursively build tables and connections from hierarchical data
     */
    const buildHierarchicalTables = (
        data: any,                       // The data object
        currentKey: string,              // Current key in the data (e.g., 'flows')
        level: number = 0,               // Current level in the hierarchy (for positioning)
        parentNodeId: string | null = null, // Parent node ID for edge connections
        parentRowIndex: number = -1,     // Index of the parent row that owns this table
        projectId: string | null = null, // Project ID for root node naming
        startNodeId: string | null = null, // ID of the start node
        occupiedPositions: Map<string, { x: number; y: number; width: number; height: number }> = new Map() // Track occupied positions
    ): { nodes: CustomNode[], edges: Edge[], startNodeId: string | null, occupiedPositions: Map<string, { x: number; y: number; width: number; height: number }> } => {
        // Check if we have data for this level
        if (!data[currentKey] || !data[currentKey].length) {
            return { nodes: [], edges: [], startNodeId, occupiedPositions };
        }

        const items = data[currentKey];
        //console.log(`Building hierarchical tables for ${currentKey} with ${items.length} items, and data: ${JSON.stringify(data)}`);
        
        // Get a sample item to determine fields
        const sampleItem = items[0] || {};
        const { displayField, descriptionField, childrenField } = determineFields(sampleItem);
        
        // Format a nice title for display
        const title = formatDisplayName(currentKey);
        
        // All nodes and edges we'll return
        let allNodes: CustomNode[] = [];
        let allEdges: Edge[] = [];
        
        // Track which row UIIDs have outgoing connections
        const connectedRowUiids = new Map<string, Set<string>>(); // nodeId -> Set of connected row UIIDs
        
        // Helper function to track a connection
        const trackConnection = (sourceNodeId: string, rowUiid: string) => {
            if (!connectedRowUiids.has(sourceNodeId)) {
                connectedRowUiids.set(sourceNodeId, new Set());
            }
            connectedRowUiids.get(sourceNodeId)!.add(rowUiid);
        };

        // Helper function to get parent node's position
        const getParentPosition = (parentId: string | null | undefined): { x: number; y: number } | null => {
            if (!parentId) return null;
            const parentNode = allNodes.find(n => n.id === parentId);
            return parentNode ? { x: parentNode.position.x, y: parentNode.position.y } : null;
        };

        // Helper function to check if a position overlaps with existing nodes
        const checkOverlap = (x: number, y: number, width: number, height: number): boolean => {
            for (const [nodeId, pos] of occupiedPositions.entries()) {
                // Check if rectangles overlap (with some padding)
                const padding = 20;
                if (!(x + width + padding < pos.x - padding || pos.x + pos.width + padding < x - padding || 
                      y + height + padding < pos.y - padding || pos.y + pos.height + padding < y - padding)) {
                    return true; // Overlap detected
                }
            }
            return false;
        };

        // Helper function to find position for children below parent
        const findChildPosition = (
            parentX: number,
            childIndex: number,
            totalChildren: number,
            width: number,
            height: number,
            targetY: number
        ): { x: number; y: number } => {
            // Y position determined by hierarchy level
            const y = targetY;
            
            // X position: spread children horizontally around parent center
            let x: number;
            if (totalChildren === 1) {
                // Single child: center it below parent
                x = parentX + (ESTIMATED_NODE_WIDTH / 2) - (width / 2);
            } else {
                // Multiple children: spread them horizontally
                const totalWidth = (totalChildren - 1) * NODE_HORIZONTAL_SPACING;
                const startX = parentX + (ESTIMATED_NODE_WIDTH / 2) - (totalWidth / 2);
                x = startX + (childIndex * NODE_HORIZONTAL_SPACING);
            }
            
            // Check for overlap and adjust if needed
            let adjustedX = x;
            let attempts = 0;
            const maxAttempts = 100;
            
            while (checkOverlap(adjustedX, y, width, height) && attempts < maxAttempts) {
                // Try shifting right first
                adjustedX += NODE_HORIZONTAL_SPACING / 2;
                attempts++;
            }
            
            return { x: adjustedX, y };
        };

        // Ensure a proposed position does not overlap existing nodes
        const ensureNonOverlappingPosition = (
            proposedX: number,
            proposedY: number,
            width: number = ESTIMATED_NODE_WIDTH,
            height: number = ESTIMATED_NODE_HEIGHT
        ): { x: number; y: number } => {
            let adjustedX = proposedX;
            let attempts = 0;
            const maxAttempts = 200;
            const shiftX = NODE_HORIZONTAL_SPACING / 2;
            let direction = 1;
            let shiftMultiplier = 1;

            while (checkOverlap(adjustedX, proposedY, width, height) && attempts < maxAttempts) {
                adjustedX = proposedX + direction * shiftMultiplier * shiftX;
                direction *= -1;
                if (direction > 0) {
                    shiftMultiplier++;
                }
                attempts++;
            }

            return { x: adjustedX, y: proposedY };
        };

        // Helper function to create flow nodes for any type
        const createFlowNodes = (
            items: any[],
            nodeType: FlowNodeType,
            nodeTypePrefix: string,
            reactFlowNodeType: string,
            parentNodeId?: string,
            parentEdgeColor?: string
        ): string[] => {
            const sortedItems = [...items].sort((a, b) => {
                const aSno = typeof a.sno === 'number' ? a.sno : (a.sno ? parseInt(a.sno) : 0);
                const bSno = typeof b.sno === 'number' ? b.sno : (b.sno ? parseInt(b.sno) : 0);
                return aSno - bSno;
            });

            console.log(`Creating ${sortedItems.length} individual ${nodeTypePrefix} nodes`);

            const createdNodeIds: string[] = [];

            // Hierarchical tree layout: Position children below their parent
            const parentPos = getParentPosition(parentNodeId);
            let xPosition: number;
            let yPosition: number;

            sortedItems.forEach((item: any, itemIndex: number) => {
                const itemUiid = item.uiid || item.id || `${nodeTypePrefix}-${itemIndex}`;
                const itemSno = typeof item.sno === 'number' ? item.sno : (item.sno ? parseInt(item.sno) : itemIndex + 1);
                const itemName = item[displayField] || item.name || `${nodeTypePrefix} ${itemSno}`;
                const itemDesc = (descriptionField && item[descriptionField]) || item.description || item.desc || '';

                // Check build status for low level requirements when creating nodes
                let buildStatus = null;
                if (nodeType === 'lowLevelRequirement' || nodeTypePrefix === 'lowlevelrequirement') {
                    const trackedBuild = projectId ? getLatestBuild(projectId, itemUiid) : null;
                    buildStatus = trackedBuild?.build_status || item.build_status || null;
                }

                const nodeId = `${nodeTypePrefix}_${itemUiid}`;
                createdNodeIds.push(nodeId);
                const targetY = getLevelYPosition(level);

                // Calculate position based on hierarchical layout
                if (parentPos) {
                    // Child nodes: position below parent, spread horizontally
                    const position = findChildPosition(
                        parentPos.x,
                        itemIndex,
                        sortedItems.length,
                        ESTIMATED_NODE_WIDTH,
                        ESTIMATED_NODE_HEIGHT,
                        targetY
                    );
                    xPosition = position.x;
                    yPosition = targetY;
                } else if (level === 0) {
                    // Root level (user flows): position below start node, spread horizontally
                    // Calculate center position for all user flows
                    const totalWidth = (sortedItems.length - 1) * NODE_HORIZONTAL_SPACING;
                    const startX = START_NODE_X - (totalWidth / 2);
                    xPosition = startX + (itemIndex * NODE_HORIZONTAL_SPACING);
                    yPosition = targetY;
                } else {
                    // Fallback: position sequentially
                    xPosition = itemIndex * NODE_HORIZONTAL_SPACING;
                    yPosition = targetY;
                }

                // Final pass to avoid overlap
                const safePosition = ensureNonOverlappingPosition(
                    xPosition,
                    yPosition,
                    ESTIMATED_NODE_WIDTH,
                    ESTIMATED_NODE_HEIGHT
                );
                xPosition = safePosition.x;
                yPosition = safePosition.y;

                // Create individual node for this item
                const flowNode: CustomNode = {
                    id: nodeId,
                    type: reactFlowNodeType,
                    data: {
                        name: itemName,
                        description: itemDesc,
                        sno: itemSno,
                        uiid: itemUiid,
                        id: itemUiid,
                        nodeType: nodeType,
                        originalData: item,
                        projectId: projectId, // Add projectId to node data
                        // Include build status if available (for low level requirements)
                        ...(buildStatus && { buildStatus })
                    },
                    position: {
                        x: xPosition,
                        y: yPosition
                    },
                    draggable: true,
                    selectable: true
                };

                allNodes.push(flowNode);
                
                // Track occupied position to prevent overlaps
                occupiedPositions.set(nodeId, {
                    x: xPosition,
                    y: yPosition,
                    width: ESTIMATED_NODE_WIDTH,
                    height: ESTIMATED_NODE_HEIGHT
                });

                // Create edge from parent to this child node (or from start node if root level)
                if (parentNodeId && parentEdgeColor) {
                    const hierarchicalEdge: Edge = {
                        id: `edge-hierarchical-${parentNodeId}-to-${nodeId}`,
                        source: parentNodeId,
                        target: nodeId,
                        sourceHandle: 'child',
                        targetHandle: 'parent',
                        animated: false,
                        style: {
                            stroke: parentEdgeColor,
                            strokeWidth: 2,
                        }
                    };
                    
                    allEdges.push(hierarchicalEdge);
                } else if (level === 0 && startNodeId) {
                    // Connect start node to root level nodes (user flows)
                    const startEdge: Edge = {
                        id: `edge-start-to-${nodeId}`,
                        source: startNodeId,
                        target: nodeId,
                        sourceHandle: 'child',
                        targetHandle: 'parent',
                        animated: false,
                        style: {
                            stroke: '#10b981',
                            strokeWidth: 2,
                        }
                    };
                    allEdges.push(startEdge);
                }
            });
            
            return createdNodeIds;
        };

        // Handle User Flows table (root level)
        // Hierarchy: Start (top) -> User Flows -> High Level Requirements -> Low Level Requirements
        // Note: Test Cases are no longer displayed as nodes, they are shown in the Low Level Requirement detail modal
        if (level === 0 && currentKey === 'flows') {
            // Step 1: Create start node at the top
            if (!startNodeId) {
                const baseStartX = -ESTIMATED_NODE_WIDTH / 2;
                const safeStartPosition = ensureNonOverlappingPosition(
                    baseStartX,
                    START_NODE_Y
                );

                const startNode: CustomNode = {
                    id: 'start-node',
                    type: 'userFlowNode',
                    data: {
                        name: 'Start',
                        description: 'Start',
                        sno: 0,
                        uiid: 'start',
                        id: 'start',
                        nodeType: 'userFlow',
                        originalData: {}
                    },
                    position: {
                        x: safeStartPosition.x,
                        y: safeStartPosition.y // Top position
                    },
                    draggable: true,
                    selectable: true
                };
                allNodes.push(startNode);
                startNodeId = 'start-node';
                
                // Track start node position
                occupiedPositions.set('start-node', {
                    x: startNode.position.x,
                    y: startNode.position.y,
                    width: ESTIMATED_NODE_WIDTH,
                    height: ESTIMATED_NODE_HEIGHT
                });
            }
            
            // Step 2: Create user flow nodes below start node
            createFlowNodes(items, 'userFlow', 'userflow', 'userFlowNode');
            
            // Step 3: Process nested High Level Requirements (HLRs) for each user flow
            items.forEach((flow: any) => {
                const flowUiid = flow.uiid || flow.id;
                const parentFlowNodeId = `userflow_${flowUiid}`;
                
                if (flow.high_level_requirement_list && flow.high_level_requirement_list.length > 0) {
                    const hlrResult = buildHierarchicalTables(
                        flow,
                        'high_level_requirement_list',
                        1, // Level 1: High Level Requirements
                        parentFlowNodeId,  // Pass parent node ID (user flow)
                        -1,
                        projectId,
                        startNodeId,
                        occupiedPositions
                    );
                    allNodes.push(...hlrResult.nodes);
                    allEdges.push(...hlrResult.edges);
                    // Merge occupied positions from children
                    hlrResult.occupiedPositions.forEach((pos, nodeId) => {
                        occupiedPositions.set(nodeId, pos);
                    });
                }
            });
        }
        // Step 4: Handle High Level Requirements (below User Flows)
        else if (currentKey === 'high_level_requirement_list' || currentKey === 'highLevelRequirements') {
            // Use the parentNodeId passed to this function (should be the User Flow node ID)
            createFlowNodes(
                items, 
                'highLevelRequirement', 
                'highlevelrequirement', 
                'highLevelRequirementNode', 
                parentNodeId || undefined,
                '#10b981' // Green color for User Flow -> HLR connections
            );
            
            // Step 5: Process nested Low Level Requirements (LLRs) for each HLR
            items.forEach((hlr: any) => {
                const hlrUiid = hlr.uiid || hlr.id;
                const parentHlrNodeId = `highlevelrequirement_${hlrUiid}`;
                
                if (hlr.low_level_requirement_list && hlr.low_level_requirement_list.length > 0) {
                    const llrResult = buildHierarchicalTables(
                        hlr,
                        'low_level_requirement_list',
                        level + 1, // Level 2: Low Level Requirements
                        parentHlrNodeId,  // Pass parent node ID (HLR)
                        -1,
                        projectId,
                        startNodeId,
                        occupiedPositions
                    );
                    allNodes.push(...llrResult.nodes);
                    allEdges.push(...llrResult.edges);
                    // Merge occupied positions from children
                    llrResult.occupiedPositions.forEach((pos, nodeId) => {
                        occupiedPositions.set(nodeId, pos);
                    });
                }
            });
        }
        // Step 6: Handle Low Level Requirements (below High Level Requirements)
        else if (currentKey === 'low_level_requirement_list' || currentKey === 'lowLevelRequirements') {
            // Use the parentNodeId passed to this function (should be the HLR node ID)
            createFlowNodes(
                items, 
                'lowLevelRequirement', 
                'lowlevelrequirement', 
                'lowLevelRequirementNode', 
                parentNodeId || undefined,
                '#3b82f6' // Blue color for HLR -> LLR connections
            );
            // Note: Test cases are no longer displayed as nodes in the canvas
            // They are shown in the Low Level Requirement detail modal instead
        }
        
        // Update all nodes with their connected row UIIDs
        const updatedNodes = allNodes.map(node => {
            const nodeConnectedRows = connectedRowUiids.get(node.id) || new Set();
            return {
                ...node,
                data: {
                    ...node.data,
                    connectedRowUiids: Array.from(nodeConnectedRows)
                }
            };
        });
        
        return {
            nodes: updatedNodes,
            edges: allEdges,
            startNodeId,
            occupiedPositions
        };
    };

    const applyRequirementsFromResponseRef = useRef<
        (response: ProjectRequirementsResponse) => Node[] | null
    >(() => null);

    applyRequirementsFromResponseRef.current = (response: ProjectRequirementsResponse) => {
        console.log('API Response:', response);
        addTerminalLog(`Received ${response.flows?.length || 0} user flows from API`);
        setUserFlows(response.flows || []);

        if (response.flows && response.flows.length > 0) {
            const { nodes: builtNodes, edges: builtEdges } = buildHierarchicalTables(
                { flows: response.flows },
                'flows',
                0,
                null,
                -1,
                projectId
            );

            const animatedNodes = builtNodes.map((node) => ({
                ...node,
                style: {
                    ...node.style,
                    opacity: 1,
                    animation: 'fadeIn 0.5s ease-in-out',
                },
            }));

            const validEdges = builtEdges.filter((edge) => {
                const sourceExists = builtNodes.some((node) => node.id === edge.source);
                const targetExists = builtNodes.some((node) => node.id === edge.target);
                if (!sourceExists || !targetExists) {
                    console.warn(`Filtering out edge ${edge.id} - source or target node doesn't exist`);
                    return false;
                }
                return true;
            });

            console.log(
                `Created ${animatedNodes.length} nodes and ${validEdges.length} valid edges (filtered out ${builtEdges.length - validEdges.length})`
            );
            addTerminalLog(`Built ${animatedNodes.length} nodes and ${validEdges.length} edges`);

            const allVisibleIds = new Set(animatedNodes.map((n) => n.id));
            const initialPositions = computeTreeLayoutPositions(animatedNodes, validEdges, allVisibleIds);
            const laidOutNodes = animatedNodes.map((node) => {
                const p = initialPositions.get(node.id);
                return p ? { ...node, position: p } : node;
            });

            setNodes(laidOutNodes);
            setEdges(validEdges);
            setShouldAutoFit(true);
            return laidOutNodes;
        }

        setNodes([]);
        setEdges([]);
        addTerminalLog('No flows found in project data');
        return [];
    };

    // Fetch data and build tables
    const fetchData = useCallback(async (force: boolean = false) => {
        if (!projectId) {
            setNodes([]);
            setEdges([]);
            setUserFlows([]);
            addTerminalLog('No project selected');
            return;
        }

        // Prevent duplicate calls (unless forced - for manual refreshes after operations)
        if (!force && isFetchingRef.current) {
            console.log('Fetch already in progress, skipping duplicate call');
            return;
        }

        isFetchingRef.current = true;
        setLoading(true);
        setLoadStage('fetching');
        setError(null);
        addTerminalLog(`Loading project data for project ID: ${projectId}`);

        try {
            setFocusedSubtreeIds(null);
            originalPositionsRef.current.clear();

            // Fade out existing nodes during loading
            setNodes((currentNodes) =>
                currentNodes.map((node) => ({
                    ...node,
                    style: {
                        ...node.style,
                        opacity: 0.5,
                        transition: 'opacity 0.3s',
                    },
                }))
            );

            const response = await apiClient<ProjectRequirementsResponse>(`/requirements/${projectId}`);
            setLoadStage('building');

            const built = applyRequirementsFromResponseRef.current(response);
            markRequirementsHydrated(projectId, refreshTrigger);
            return built;
        } catch (error: any) {
            console.error('Error loading project data:', error);
            setError(`Failed to load project data: ${error.message || 'Unknown error'}`);
            addTerminalLog(`Error loading project data: ${error.message || 'Unknown error'}`);
            return null;
        } finally {
            setLoading(false);
            setLoadStage(null);
            isFetchingRef.current = false;
        }
    }, [projectId, refreshTrigger, setNodes, setEdges, setUserFlows, setLoading, setError, addTerminalLog]);

    const syncSelectedFlowNodeFromNodes = useCallback((latestNodes: Node[], targetUiid: string | null) => {
        if (!targetUiid) return;
        const n = latestNodes.find(
            (node) =>
                FLOW_NODE_TYPES.includes(node.type as (typeof FLOW_NODE_TYPES)[number]) &&
                (((node.data as { uiid?: string }).uiid === targetUiid) ||
                    ((node.data as { id?: string }).id === targetUiid))
        );
        const fd = n ? flowNodeDataFromCanvasNode(n) : null;
        if (fd) setSelectedFlowNode(fd);
    }, []);

    const appendGenerateProgressLine = useCallback((line: string) => {
        setGenerateProgressLines((prev) => [...prev, line]);
        addTerminalLog(line);
    }, [addTerminalLog]);

    const closeGenerateProgressModal = useCallback(() => {
        if (autoCloseProgressModalTimerRef.current) {
            clearTimeout(autoCloseProgressModalTimerRef.current);
            autoCloseProgressModalTimerRef.current = null;
        }
        const wasSuccess = generateProgressStatusRef.current === 'success';
        const pendingToast = completionToastPendingRef.current;
        completionToastPendingRef.current = null;
        if (wasSuccess && pendingToast) {
            setToastMessage(pendingToast);
            if (toastClearTimerRef.current) {
                clearTimeout(toastClearTimerRef.current);
            }
            toastClearTimerRef.current = setTimeout(() => {
                setToastMessage(null);
                toastClearTimerRef.current = null;
            }, 5000);
        }
        setGenerateProgressOpen(false);
        setGenerateProgressLines([]);
        setGenerateProgressError(null);
        setGenerateProgressContextTitle('');
        setGenerateProgressStatus('running');
    }, []);

    // Generate requirements for specific row
    const handleGenerateRequirements = async (nodeId: string, rowIndex: number): Promise<void> => {
        if (!projectId) {
            console.error("Cannot generate requirements: No project ID");
            setError("Cannot generate requirements: No project ID");
            return;
        }
        
        console.log(`Generating requirements for nodeId=${nodeId}, rowIndex=${rowIndex}, projectId=${projectId}`);
        setError(null);
        addTerminalLog(`Generating requirements for nodeId=${nodeId}, rowIndex=${rowIndex}, projectId=${projectId}`);
        
        // Show loading state in the node
        setNodes(nodes.map(node => 
            node.id === nodeId 
                ? { 
                    ...node, 
                    data: { 
                        ...node.data, 
                        title: `${node.data.title} (Generating...)` 
                    } 
                } 
                : node
        ));
        
        try {
            // Find the node and get its data
            const node = nodes.find(n => n.id === nodeId);
            if (!node) {
                console.error(`Node with ID ${nodeId} not found`);
                throw new Error(`Node with ID ${nodeId} not found`);
            }
            
            const nodeData = node.data;
            const componentId = nodeData.componentId;
            
            console.log(`Node found: ${nodeId}, componentId=${componentId}`);
            
            if (!componentId) {
                console.error('Component ID not found for this table');
                throw new Error('Component ID not found for this table');
            }
            
            // Get the visible rows and allRows from node data
            const visibleRows: TableRowData[] = nodeData.rows || [];
            const allRows: TableRowData[] = nodeData.allRows || visibleRows;
            const isMinimized = nodeData.isMinimized || false;
            
            console.log(`Table state: isMinimized=${isMinimized}, visibleRows=${visibleRows.length}, allRows=${allRows.length}`);
            
            // Validate row index
            if (rowIndex < 0 || rowIndex >= visibleRows.length) {
                console.error(`Row index ${rowIndex} is out of bounds (0-${visibleRows.length-1})`);
                throw new Error(`Row index ${rowIndex} is out of bounds`);
            }
            
            // Get the row data for the clicked row
            const rowData = visibleRows[rowIndex];
            console.log(`Found row at index ${rowIndex} in visible rows:`, rowData);
            
            // If minimized, find the actual index in allRows for reference
            let effectiveRowIndex = rowIndex;
            if (isMinimized) {
                const foundIndex = allRows.findIndex(r => r.id === rowData.id);
                if (foundIndex >= 0) {
                    effectiveRowIndex = foundIndex;
                    console.log(`Row's actual index in allRows: ${effectiveRowIndex}`);
                }
            }
            
            // Get the UIID from the row data
            const rowUiid = rowData.uiid || rowData.id;
            
            // Verify which row we're processing
            console.log(`GENERATING FOR ROW ${rowIndex} (effective ${effectiveRowIndex}):`);
            console.log(`Row name: ${rowData.name}`);
            console.log(`Row UIID: ${rowUiid}`);
            console.log(`Original data:`, rowData.originalData);

            setGenerateProgressContextTitle(rowData.name || rowUiid);
            setGenerateProgressLines([]);
            setGenerateProgressError(null);
            completionToastPendingRef.current = null;
            if (autoCloseProgressModalTimerRef.current) {
                clearTimeout(autoCloseProgressModalTimerRef.current);
                autoCloseProgressModalTimerRef.current = null;
            }
            setGenerateProgressStatus('running');
            setGenerateProgressOpen(true);
            
            // Prepare text to send to LLM
            const text = `
                Item Name: ${rowData.name}
                Description: ${rowData.desc || ''}
                Additional Context: This is from the ${nodeData.title} table.
                Row Index: ${effectiveRowIndex}
                Row UIID: ${rowUiid}
            `;
            
            console.log(`Calling LLM service with componentId=${componentId}, projectId=${projectId}, parent_uiid=${rowUiid}`);
            const response = await LlmService.processWithLlm(
                componentId.split('_').slice(0, 2).join('_'),
                text,
                projectId,
                true, // Save to database
                (update: string) => {
                    console.log("LLM progress update:", update);
                    appendGenerateProgressLine(update);
                },
                rowUiid // Pass the UIID as parent_uiid
            );
            
            console.log("LLM response received:", response);
            if (response.result) {
                console.log("LLM result:", response.result.substring(0, 200) + "...");
            }
            console.log("Backend-generated UIIDs:", response.generated_uiids || []);
            const nNew = response.generated_uiids?.length ?? 0;
            appendGenerateProgressLine(
                `Requirements generated successfully. ${nNew} new item(s).`
            );
            if (response.result) {
                const maxLen = 50_000;
                const r =
                    response.result.length > maxLen
                        ? `${response.result.slice(0, maxLen)}\n…(truncated)`
                        : response.result;
                appendGenerateProgressLine('--- Model output ---');
                appendGenerateProgressLine(r);
            }
            addTerminalLog(`Requirements generated successfully. Generated ${nNew} new items`);
            
            // Show success feedback
            const originalTitle = nodeData.title;
            setNodes(nodes.map(node => 
                node.id === nodeId 
                    ? { 
                        ...node, 
                        data: { 
                            ...node.data, 
                            title: `${originalTitle} (Requirements Generated!)` 
                    } 
                } 
                    : node
            ));
            
            // Re-fetch data to show updated requirements
            console.log("Refreshing data to show new requirements");
            addTerminalLog('Refreshing canvas to show new requirements');
            const freshNodes = await fetchData(true);
            const { isOpen: modalOpen, uiid: openUiid } = flowModalSyncRef.current;
            if (modalOpen && openUiid && openUiid === rowUiid && freshNodes) {
                syncSelectedFlowNodeFromNodes(freshNodes, openUiid);
            }
            completionToastPendingRef.current = getChildGenerationCompletionMessage(
                'table',
                undefined,
                nodeId
            );
            setGenerateProgressStatus('success');
            if (autoCloseProgressModalTimerRef.current) {
                clearTimeout(autoCloseProgressModalTimerRef.current);
            }
            autoCloseProgressModalTimerRef.current = setTimeout(() => {
                autoCloseProgressModalTimerRef.current = null;
                closeGenerateProgressModal();
            }, 3000);
            
            // Reset title after delay
            setTimeout(() => {
                setNodes(nodes => nodes.map(node => 
                    node.id === nodeId 
                        ? { 
                            ...node, 
                            data: { 
                                ...node.data, 
                                title: originalTitle
                            } 
                        } 
                        : node
                ));
            }, 3000);
            
        } catch (error: any) {
            console.error('Error generating requirements:', error);
            const msg = error.message || 'Unknown error';
            setError(`Failed to generate requirements: ${msg}`);
            addTerminalLog(`Error generating requirements: ${msg}`);
            completionToastPendingRef.current = null;
            if (autoCloseProgressModalTimerRef.current) {
                clearTimeout(autoCloseProgressModalTimerRef.current);
                autoCloseProgressModalTimerRef.current = null;
            }
            setGenerateProgressOpen(true);
            appendGenerateProgressLine(`Error: ${msg}`);
            setGenerateProgressError(msg);
            setGenerateProgressStatus('error');
            
            // Reset nodes to original state
            await fetchData(true);
        }
    };

    // Update nodeActionsRef to include onGenerateChildReq after it's defined
    nodeActionsRef.current.onGenerateChildReq = handleGenerateRequirements;

    /** Parent uiid for a sibling at the same level (user flow for HLR, HLR for LLR). */
    const resolveSiblingParentUiid = useCallback(
        (nodeType: FlowNodeType, sourceNode: FlowNodeData | null): string | null => {
            if (nodeType === 'userFlow') return null;
            const fromOriginal = (sourceNode?.originalData as { parent_uiid?: string } | undefined)
                ?.parent_uiid;
            if (fromOriginal) return fromOriginal;
            if (!sourceNode?.uiid) return null;
            const nodeId =
                nodeType === 'highLevelRequirement'
                    ? `highlevelrequirement_${sourceNode.uiid}`
                    : nodeType === 'lowLevelRequirement'
                      ? `lowlevelrequirement_${sourceNode.uiid}`
                      : null;
            if (!nodeId) return null;
            const parentEdge = edges.find((e) => e.target === nodeId);
            if (!parentEdge) return null;
            const parentNode = nodes.find((n) => n.id === parentEdge.source);
            return (parentNode?.data as { uiid?: string } | undefined)?.uiid ?? null;
        },
        [nodes, edges]
    );

    // Handler for adding a new requirement (sibling at the same level)
    const handleAddRequirement = useCallback(
        (_contextUiid: string, nodeType: FlowNodeType) => {
            const resolvedParent = resolveSiblingParentUiid(nodeType, selectedFlowNode);
            if (nodeType !== 'userFlow' && !resolvedParent) {
                setError('Cannot add a sibling requirement: parent not found.');
                return;
            }
            setAddRequirementIntent('sibling');
            setAddRequirementParentUiid(resolvedParent);
            setAddRequirementType(nodeType);
            setNewRequirementName('');
            setNewRequirementDescription('');
            setError(null);
            setIsAddRequirementModalOpen(true);
        },
        [selectedFlowNode, resolveSiblingParentUiid]
    );

    // Handler for adding a child requirement manually (under the current node)
    const handleAddChildRequirement = useCallback(
        (parentUiid: string, parentNodeType: FlowNodeType) => {
            const childType = childNodeTypeForParent(parentNodeType);
            if (!childType) {
                setError('Cannot add a child requirement for this node type.');
                return;
            }
            setAddRequirementIntent('child');
            setAddRequirementParentUiid(parentUiid);
            setAddRequirementType(childType);
            setNewRequirementName('');
            setNewRequirementDescription('');
            setError(null);
            setIsAddRequirementModalOpen(true);
        },
        []
    );

    /** Resolve project id from chat session localStorage, then canvas prop. */
    const resolveProjectIdForCreate = useCallback((): string => {
        const fromSession = getCurrentProjectIdFromLocalStorage();
        if (fromSession) return fromSession;
        if (projectId != null && String(projectId).trim()) {
            return String(projectId).trim();
        }
        throw new Error('No project selected (open a project or start a chat session)');
    }, [projectId]);

    /** Map canvas node level → DynamoDB table / create API (manual creates). */
    const createRequirementForNodeLevel = useCallback(
        async (
            nodeType: FlowNodeType,
            parentUiid: string | null,
            name: string,
            description: string | undefined
        ) => {
            const projectIdStr = resolveProjectIdForCreate();
            const uiid = generateManualRequirementUiid();
            const base = {
                uiid,
                name,
                description,
                project_id: projectIdStr,
            };

            switch (nodeType) {
                case 'userFlow':
                    return RequirementsService.createUserFlow(projectIdStr, base);
                case 'highLevelRequirement': {
                    if (!parentUiid) {
                        throw new Error('Parent user flow not found for high-level requirement');
                    }
                    return RequirementsService.createHighLevelRequirement(projectIdStr, {
                        ...base,
                        parent_uiid: parentUiid,
                    });
                }
                case 'lowLevelRequirement': {
                    if (!parentUiid) {
                        throw new Error('Parent high-level requirement not found for low-level requirement');
                    }
                    return RequirementsService.createLowLevelRequirement(projectIdStr, {
                        ...base,
                        parent_uiid: parentUiid,
                    });
                }
                case 'testCase': {
                    if (!parentUiid) {
                        throw new Error('Parent low-level requirement not found for test case');
                    }
                    return RequirementsService.createTestCase(projectIdStr, {
                        ...base,
                        parent_uiid: parentUiid,
                    });
                }
                default:
                    throw new Error(`Cannot create requirement at node level: ${nodeType}`);
            }
        },
        [resolveProjectIdForCreate]
    );

    // Handler for creating a new requirement
    const handleCreateRequirement = useCallback(async () => {
        if (!addRequirementType || !newRequirementName.trim()) {
            setError('Please provide a name for the requirement');
            return;
        }
        try {
            resolveProjectIdForCreate();
        } catch {
            setError('No project selected (open a project or start a chat session)');
            return;
        }
        if (
            addRequirementType !== 'userFlow' &&
            !addRequirementParentUiid
        ) {
            setError('Cannot create requirement: parent not found.');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            addTerminalLog(`Creating new ${addRequirementType} requirement...`);

            await createRequirementForNodeLevel(
                addRequirementType,
                addRequirementParentUiid,
                newRequirementName.trim(),
                newRequirementDescription.trim() || undefined
            );

            const wasChildCreate = addRequirementIntent === 'child';

            addTerminalLog(`Successfully created ${addRequirementType} requirement`);
            setIsAddRequirementModalOpen(false);
            setNewRequirementName('');
            setNewRequirementDescription('');
            setAddRequirementIntent('sibling');

            const openUiid = flowModalSyncRef.current.uiid;
            const freshNodes = await fetchData(true);
            if (
                flowModalSyncRef.current.isOpen &&
                openUiid &&
                freshNodes &&
                wasChildCreate
            ) {
                syncSelectedFlowNodeFromNodes(freshNodes, openUiid);
            }
        } catch (error: any) {
            console.error('Error creating requirement:', error);
            setError(`Failed to create requirement: ${error.message || 'Unknown error'}`);
            addTerminalLog(`Error creating requirement: ${error.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    }, [
        resolveProjectIdForCreate,
        addRequirementParentUiid,
        addRequirementType,
        addRequirementIntent,
        newRequirementName,
        newRequirementDescription,
        createRequirementForNodeLevel,
        fetchData,
        addTerminalLog,
        syncSelectedFlowNodeFromNodes,
    ]);

    // Handler for navigating to child requirement details
    const handleNavigateToChild = useCallback((childUiid: string, childNodeType: FlowNodeType) => {
        // Find the node with this UIID
        const nodeTypePrefix = 
            childNodeType === 'userFlow' ? 'userflow_' :
            childNodeType === 'highLevelRequirement' ? 'highlevelrequirement_' :
            childNodeType === 'lowLevelRequirement' ? 'lowlevelrequirement_' :
            'testcase_';
        
        const childNode = nodes.find(n => n.id === `${nodeTypePrefix}${childUiid}` || (n.data as any)?.uiid === childUiid);
        
        if (childNode && childNode.data) {
            const nodeData: FlowNodeData = {
                name: (childNode.data as any).name || '',
                description: (childNode.data as any).description || '',
                sno: (childNode.data as any).sno || 0,
                uiid: (childNode.data as any).uiid || childUiid,
                id: childNode.id,
                originalData: (childNode.data as any).originalData,
                nodeType: childNodeType
            };
            
            setSelectedFlowNode(nodeData);
            setIsFlowModalOpen(true);
        }
    }, [nodes]);

    // Deep link: open a specific node (and optionally highlight comment) when coming from notification
    useEffect(() => {
        if (!deepLinkOpen || nodes.length === 0 || !onDeepLinkHandled) return;
        const expectedNodeType = targetTypeToNodeType[deepLinkOpen.targetType];
        const targetUiid = deepLinkOpen.targetUiid;
        const node = nodes.find(
            (n) =>
                (n.data as any)?.uiid === targetUiid &&
                n.type === expectedNodeType
        );
        if (node && node.data) {
            const flowNodeType = targetTypeToFlowNodeType[deepLinkOpen.targetType] ?? 'userFlow';
            const nodeData: FlowNodeData = {
                name: (node.data as any).name || '',
                description: (node.data as any).description || '',
                sno: (node.data as any).sno || 0,
                uiid: (node.data as any).uiid || targetUiid,
                id: node.id,
                originalData: (node.data as any).originalData,
                nodeType: flowNodeType
            };
            setDeepLinkCommentId(deepLinkOpen.commentId ?? null);
            setSelectedFlowNode(nodeData);
            setIsFlowModalOpen(true);
            onDeepLinkHandled();
        } else {
            onDeepLinkHandled();
        }
    }, [deepLinkOpen, nodes, onDeepLinkHandled]);

    const handleFlowSave = useCallback(async (nodeData: FlowNodeData, updatedData: Partial<FlowNodeData>) => {
        if (!projectId) {
            setError('No project ID available');
            return;
        }

        const uiid = nodeData.uiid || (nodeData.originalData as any)?.uiid;
        if (!uiid) {
            setError('Cannot save: missing backend UIID');
            return;
        }

        const payload = {
            name: updatedData.name,
            description: updatedData.description,
        };

        if (payload.name === undefined && payload.description === undefined) {
            return;
        }

        try {
            setLoading(true);
            setError(null);
            addTerminalLog(`Saving ${nodeData.nodeType || 'requirement'} changes...`);

            if (nodeData.nodeType === 'userFlow') {
                await RequirementsService.updateUserFlow(projectId, uiid, payload);
            } else if (nodeData.nodeType === 'highLevelRequirement') {
                await RequirementsService.updateHighLevelRequirement(projectId, uiid, payload);
            } else if (nodeData.nodeType === 'lowLevelRequirement') {
                await RequirementsService.updateLowLevelRequirement(projectId, uiid, payload);
            } else if (nodeData.nodeType === 'testCase') {
                await RequirementsService.updateTestCase(projectId, uiid, payload);
            } else {
                throw new Error(`Unsupported node type for save: ${nodeData.nodeType}`);
            }

            // Keep modal state responsive before full refresh.
            applyLocalFlowSave(nodeData.id, updatedData);
            addTerminalLog(`Saved ${nodeData.nodeType || 'requirement'} changes successfully`);
            await fetchData(true);
        } catch (error: any) {
            console.error('Error updating requirement:', error);
            setError(`Failed to update requirement: ${error.message || 'Unknown error'}`);
            addTerminalLog(`Error updating requirement: ${error.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    }, [projectId, applyLocalFlowSave, fetchData]);

    // Handler for deleting a requirement
    const handleDeleteRequirement = useCallback(async (uiid: string, nodeType: FlowNodeType) => {
        if (!projectId) {
            setError('No project ID available');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            addTerminalLog(`Deleting ${nodeType} requirement...`);

            if (nodeType === 'userFlow') {
                await RequirementsService.deleteUserFlow(projectId, uiid);
            } else if (nodeType === 'highLevelRequirement') {
                await RequirementsService.deleteHighLevelRequirement(projectId, uiid);
            } else if (nodeType === 'lowLevelRequirement') {
                await RequirementsService.deleteLowLevelRequirement(projectId, uiid);
            }

            addTerminalLog(`Successfully deleted ${nodeType} requirement`);
            
            // Refresh the canvas
            await fetchData(true); // Force refresh after deleting requirement
        } catch (error: any) {
            console.error('Error deleting requirement:', error);
            setError(`Failed to delete requirement: ${error.message || 'Unknown error'}`);
            addTerminalLog(`Error deleting requirement: ${error.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    // Handler for generating child requirements from FlowDetailModal
    // Works directly with FlowNodes - no table context needed
    const handleGenerateChildReqFromFlowNode = useCallback(async (uiid: string) => {
        if (!projectId) {
            console.error("Cannot generate requirements: No project ID");
            setError("Cannot generate requirements: No project ID");
            return;
        }
        
        console.log(`Generating child requirements for FlowNode uiid: ${uiid}`);
        setError(null);
        addTerminalLog(`Generating child requirements for uiid: ${uiid}`);
        
        try {
            // Find the FlowNode by uiid
            const flowNode = nodes.find(n => {
                const nodeData = n.data as any;
                const nodeUiid = nodeData.uiid || nodeData.id;
                return nodeUiid === uiid && ['userFlowNode', 'highLevelRequirementNode', 'lowLevelRequirementNode'].includes(n.type || '');
            });
            
            if (!flowNode) {
                console.error(`FlowNode with uiid ${uiid} not found`);
                throw new Error(`FlowNode with uiid ${uiid} not found`);
            }
            
            const flowNodeData = flowNode.data as any;
            const flowNodeUiid = flowNodeData.uiid || flowNodeData.id;
            const flowNodeName = flowNodeData.name || 'Unnamed';
            const flowNodeDescription = flowNodeData.description || flowNodeData.desc || '';
            
            // Map node type to the correct componentId for generating child requirements
            // Based on backend config.json componentModelMapping
            const getComponentIdForNodeType = (nodeType: string): string => {
                switch (nodeType) {
                    case 'userFlowNode':
                        return 'flow_genChildReq'; // Generates high-level requirements
                    case 'highLevelRequirementNode':
                        return 'highlevelrequirement_genChildReq'; // Generates low-level requirements
                    case 'lowLevelRequirementNode':
                        return 'lowlevelrequirement_genChildReq'; // Generates test cases (now shown in modal, not as nodes)
                    default:
                        throw new Error(`Unknown node type: ${nodeType}`);
                }
            };
            
            const componentId = getComponentIdForNodeType(flowNode.type || '');
            
            console.log(`FlowNode found: id=${flowNode.id}, type=${flowNode.type}, uiid=${flowNodeUiid}, name=${flowNodeName}, componentId=${componentId}`);

            setGenerateProgressContextTitle(flowNodeName);
            setGenerateProgressLines([]);
            setGenerateProgressError(null);
            setGenerateProgressStatus('running');
            setGenerateProgressOpen(true);
            
            // Prepare text to send to LLM
            const text = `
                Item Name: ${flowNodeName}
                Description: ${flowNodeDescription}
                Additional Context: This is a ${flowNode.type} node.
                Node UIID: ${flowNodeUiid}
            `;
            
            console.log(`Calling LLM service with componentId=${componentId}, projectId=${projectId}, parent_uiid=${flowNodeUiid}`);
            const response = await LlmService.processWithLlm(
                componentId,
                text,
                projectId,
                true, // Save to database
                (update: string) => {
                    console.log("LLM progress update:", update);
                    appendGenerateProgressLine(update);
                },
                flowNodeUiid // Pass the UIID as parent_uiid
            );
            
            console.log("LLM response received:", response);
            if (response.result) {
                console.log("LLM result:", response.result.substring(0, 200) + "...");
            }
            console.log("Backend-generated UIIDs:", response.generated_uiids || []);
            const nNew = response.generated_uiids?.length ?? 0;
            appendGenerateProgressLine(
                `Requirements generated successfully. ${nNew} new item(s).`
            );
            if (response.result) {
                const maxLen = 50_000;
                const r =
                    response.result.length > maxLen
                        ? `${response.result.slice(0, maxLen)}\n…(truncated)`
                        : response.result;
                appendGenerateProgressLine('--- Model output ---');
                appendGenerateProgressLine(r);
            }
            addTerminalLog(`Requirements generated successfully. Generated ${nNew} new items`);
            
            // Re-fetch data to show updated requirements
            console.log("Refreshing data to show new requirements");
            addTerminalLog('Refreshing canvas to show new requirements');
            const freshNodes = await fetchData(true);
            const { isOpen: modalOpen, uiid: openUiid } = flowModalSyncRef.current;
            if (modalOpen && openUiid && openUiid === flowNodeUiid && freshNodes) {
                syncSelectedFlowNodeFromNodes(freshNodes, openUiid);
            }
            completionToastPendingRef.current = getChildGenerationCompletionMessage(
                'flow',
                flowNode.type || undefined
            );
            setGenerateProgressStatus('success');
            if (autoCloseProgressModalTimerRef.current) {
                clearTimeout(autoCloseProgressModalTimerRef.current);
            }
            autoCloseProgressModalTimerRef.current = setTimeout(() => {
                autoCloseProgressModalTimerRef.current = null;
                closeGenerateProgressModal();
            }, 3000);
            
        } catch (error: any) {
            console.error('Error generating child requirements:', error);
            const msg = error.message || 'Unknown error';
            setError(`Failed to generate child requirements: ${msg}`);
            addTerminalLog(`Error generating child requirements: ${msg}`);
            completionToastPendingRef.current = null;
            if (autoCloseProgressModalTimerRef.current) {
                clearTimeout(autoCloseProgressModalTimerRef.current);
                autoCloseProgressModalTimerRef.current = null;
            }
            setGenerateProgressOpen(true);
            appendGenerateProgressLine(`Error: ${msg}`);
            setGenerateProgressError(msg);
            setGenerateProgressStatus('error');
            
            // Reset nodes to original state
            await fetchData(true);
        }
    }, [
        nodes,
        projectId,
        setError,
        fetchData,
        appendGenerateProgressLine,
        syncSelectedFlowNodeFromNodes,
        addTerminalLog,
        closeGenerateProgressModal
    ]);

    // Build requirements object by traversing the hierarchy
    const buildRequirementsObject = async (llrUiid: string, llrData: TableRowData, nodeData: any, nodeId: string) => {
        const requirements: any = {
            user_flow: {},
            high_level_requirements: {},
            low_level_requirements: {},
            test_cases: {}
        };
        
        try {
            console.log('LLR Data:', llrData);
            console.log('Node Data:', nodeData);
            console.log('Building requirements for LLR:', llrUiid, llrData.name);
            
            // Step 1: Add the selected LLR
            const llr = llrData.originalData || llrData;
            requirements.low_level_requirements = {
                uiid: llrUiid,
                name: llrData.name,
                description: llrData.desc || '',
                parent_uiid: llr.parent_uiid
            };
            
            console.log('Step 1 - Added LLR:', llrUiid, llrData.name);
            console.log('LLR parent_uiid:', llr.parent_uiid);
            
            // Step 2: Get Test Cases from LLR's test_case_list
            const testCasesForLlr: any[] = [];
            
            console.log(`Getting test cases from LLR's test_case_list for ${llrUiid}`);
            console.log(`LLR original data:`, llr);
            
            if (llr.test_case_list && Array.isArray(llr.test_case_list)) {
                console.log(`Found ${llr.test_case_list.length} test cases in LLR's test_case_list`);
                
                llr.test_case_list.forEach((testCase: any) => {
                    console.log(`Processing test case from LLR: ${testCase.uiid} - ${testCase.name}`);
                    
                    testCasesForLlr.push({
                        uiid: testCase.uiid,
                        name: testCase.name,
                        description: testCase.description || '',
                        parent_uiid: testCase.parent_uiid
                    });
                    console.log(`✅ Added test case: ${testCase.uiid} - ${testCase.name}`);
                });
            } else {
                console.log('No test_case_list found in LLR original data');
            }
            
            requirements.test_cases = testCasesForLlr;
            console.log(`Step 2 - Found ${testCasesForLlr.length} Test Cases for LLR ${llrUiid}`);
            
            // Step 3: Find HLR where LLR.parent_uiid == HLR.uiid
            const hlrNodes = nodes.filter(n => n.id.startsWith('highlevelrequirement_'));
            let parentHlr: any = null;
            
            hlrNodes.forEach(hlrNode => {
                const hlrData = hlrNode.data;
                const hlrRows = hlrData.allRows || hlrData.rows || [];
                
                            hlrRows.forEach((hlr: any) => {
                const hlrUiid = hlr.uiid || hlr.id;
                console.log(`Checking HLR ${hlrUiid}: uiid=${hlrUiid}, LLR.parent_uiid=${llr.parent_uiid}`);
                console.log(`HLR data:`, hlr);
                
                if (llr.parent_uiid === hlrUiid) {
                    parentHlr = hlr;
                    console.log(`✅ Found parent HLR: ${hlrUiid} - ${hlr.name}`);
                }
            });
            });
            
            if (parentHlr) {
                const hlrUiid = parentHlr.uiid || parentHlr.id;
                console.log('HLR full data:', parentHlr);
                console.log('HLR parent_uiid from data:', parentHlr.originalData.parent_uiid);
                
                requirements.high_level_requirements = {
                    uiid: hlrUiid,
                    name: parentHlr.name,
                    description: parentHlr.desc || '',
                    parent_uiid: parentHlr.originalData.parent_uiid || null
                };
                console.log(`Step 3 - Added HLR: ${hlrUiid} - ${parentHlr.name}`);
                console.log('HLR parent_uiid:', parentHlr.originalData.parent_uiid);
            } else {
                console.log('❌ No parent HLR found for LLR:', llrUiid);
            }
            
            // Step 4: Find User Flow where HLR.parent_uiid == User_Flow.uiid
            let parentFlow: any = null;
            if (parentHlr && parentHlr.originalData.parent_uiid) {
                // First try to find in the userFlows state (from API)
                parentFlow = userFlows.find(flow => flow.uiid === parentHlr.originalData.parent_uiid);
                
                if (parentFlow) {
                    console.log(`✅ Found parent User Flow in userFlows: ${parentFlow.uiid} - ${parentFlow.name}`);
                } else {
                    // Fallback: try to find in flow nodes (now individual userFlowNode types)
                    const flowNodes = nodes.filter(n => n.id.startsWith('userflow_') && n.type === 'userFlowNode');
                    
                    flowNodes.forEach(flowNode => {
                        const flowData = flowNode.data as any; // FlowNodeData type
                        const flowUiid = flowData.uiid || flowData.id;
                        console.log(`Checking User Flow ${flowUiid}: uiid=${flowUiid}, HLR.parent_uiid=${parentHlr.originalData?.parent_uiid}`);
                        
                        if (parentHlr.originalData?.parent_uiid === flowUiid) {
                            parentFlow = {
                                uiid: flowUiid,
                                id: flowData.id,
                                name: flowData.name,
                                description: flowData.description,
                                desc: flowData.description,
                                originalData: flowData.originalData
                            };
                            console.log(`✅ Found parent User Flow in nodes: ${flowUiid} - ${flowData.name}`);
                        }
                    });
                }
                
                if (parentFlow) {
                    const flowUiid = parentFlow.uiid || parentFlow.id;
                    requirements.user_flow = {
                        uiid: flowUiid,
                        name: parentFlow.name,
                        description: parentFlow.desc || parentFlow.description || '',
                        parent_uiid: parentFlow.parent_uiid
                    };
                    console.log(`Step 4 - Added User Flow: ${flowUiid} - ${parentFlow.name}`);
                } else {
                    console.log('❌ No parent User Flow found for HLR:', parentHlr.uiid);
                    console.log('HLR parent_uiid:', parentHlr.parent_uiid);
                    console.log('Available userFlows:', userFlows.map(f => ({ uiid: f.uiid, name: f.name })));
                }
            } else {
                console.log('❌ Cannot find User Flow: HLR has no parent_uiid');
                console.log('HLR data:', parentHlr);
            }
            
            console.log('Final requirements object:', requirements);
            console.log(`Summary: LLR=${llrUiid}, HLR=${parentHlr ? parentHlr.uiid : 'Not found'}, User Flow=${parentFlow ? parentFlow.uiid : 'Not found'}, Test Cases=${testCasesForLlr.length}`);
            console.log('Available userFlows:', userFlows.map(f => ({ uiid: f.uiid, name: f.name })));
            console.log('All nodes:', nodes.map(n => ({ id: n.id, type: n.type })));
            
            return requirements;
            
        } catch (error) {
            console.error('Error building requirements object:', error);
            return requirements;
        }
    };

    // Build epic requirements object for High Level Requirements
    // Structure: high_level_requirements with nested low_level_requirements as object
    const buildEpicRequirementsObject = async (hlrUiid: string, hlrData: TableRowData, nodeData: any, nodeId: string) => {
        const requirements: any = {
            high_level_requirements: {
                uiid: hlrUiid,
                name: hlrData.name,
                description: hlrData.desc || '',
                low_level_requirements: {}
            }
        };
        
        try {
            console.log('HLR Data:', hlrData);
            console.log('Node Data:', nodeData);
            console.log('Building epic requirements for HLR:', hlrUiid, hlrData.name);
            
            // Get the HLR's low_level_requirement_list
            const hlr = hlrData.originalData || hlrData;
            const llrList = hlr.low_level_requirement_list || [];
            
            console.log(`Found ${llrList.length} Low Level Requirements for HLR ${hlrUiid}`);
            
            // Build low_level_requirements object with llr_1, llr_2, etc. keys
            let llrIndex = 1;
            for (const llr of llrList) {
                const llrKey = `llr_${llrIndex}`;
                const llrUiid = llr.uiid || llr.id;
                
                console.log(`Processing LLR ${llrKey}: ${llrUiid} - ${llr.name}`);
                
                // Get test cases for this LLR
                const testCasesObj: Record<string, { name: string; desc: string }> = {};
                const testCaseList = llr.test_case_list || [];
                
                let tcIndex = 1;
                for (const testCase of testCaseList) {
                    const tcKey = `tc_${tcIndex}`;
                    testCasesObj[tcKey] = {
                        name: testCase.name || '',
                        desc: testCase.description || testCase.desc || ''
                    };
                    console.log(`  Added test case ${tcKey}: ${testCase.name}`);
                    tcIndex++;
                }
                
                // Build LLR object with test_cases
                requirements.high_level_requirements.low_level_requirements[llrKey] = {
                    uiid: llrUiid,
                    name: llr.name || '',
                    description: llr.description || llr.desc || '',
                    test_cases: testCasesObj
                };
                
                console.log(`✅ Added LLR ${llrKey} with ${Object.keys(testCasesObj).length} test cases`);
                llrIndex++;
            }
            
            console.log('Final epic requirements object:', requirements);
            console.log(`Summary: HLR=${hlrUiid}, LLRs=${llrList.length}`);
            
            return requirements;
            
        } catch (error) {
            console.error('Error building epic requirements object:', error);
            return requirements;
        }
    };

    const ensureChatPanelOpen = useCallback(() => {
        if (!rightPanelOpen && onToggleRightPanel) {
            onToggleRightPanel();
        }
    }, [rightPanelOpen, onToggleRightPanel]);

    const { handleBuildFeature, handleBuildEpic } = useVishCoderActions({
        projectId,
        nodes,
        setNodes,
        bottomPanelOpen,
        onToggleBottomPanel,
        ensureChatPanelOpen,
        openContractModal,
        isRightPanelOpen,
        addTerminalLog,
        setError,
        setGenerateLoading,
        buildRequirementsObject,
        buildEpicRequirementsObject,
        setBuildSummaryData,
        setIsBuildSummaryModalOpen,
    });

    // Panel toggle functions
    const toggleLeftPanel = useCallback(() => {
        console.log('CanvasViewer: toggleLeftPanel called');
        if (onToggleSidebar) {
            console.log('CanvasViewer: calling onToggleSidebar');
            onToggleSidebar();
        } else {
            console.log('CanvasViewer: onToggleSidebar is not provided');
        }
    }, [onToggleSidebar]);

    const toggleBottomPanel = useCallback(() => {
        if (onToggleBottomPanel) {
            onToggleBottomPanel();
        }
    }, [onToggleBottomPanel]);

    const toggleRightPanel = useCallback(() => {
        if (onToggleRightPanel) {
            onToggleRightPanel();
        }
    }, [onToggleRightPanel]);

    const handleFlowInit = useCallback((instance: ReactFlowInstance) => {
        setReactFlowInstance(instance);
    }, []);


    // Communication Protocol Handler - uses the centralized comm.ts module
    // This replaces the old generateVishCoderPayload function with the standardized approach
    // All payloads now use the new 'body' structure and proper session management















    useEffect(() => {
        if (!projectId) return;
        for (const k of [...requirementsHydratedKeys]) {
            if (!k.startsWith(`${projectId}:`)) {
                requirementsHydratedKeys.delete(k);
            }
        }
    }, [projectId]);

    // Fetch or hydrate from parent seed (single GET at parent when opening a project with flows)
    useEffect(() => {
        if (!projectId) {
            return;
        }

        const cacheKeyReady = isRequirementsHydrated(projectId, refreshTrigger);

        const hasSeed =
            initialRequirements != null && (initialRequirements.flows?.length ?? 0) > 0;

        if (hasSeed && initialRequirements) {
            if (cacheKeyReady) {
                onInitialRequirementsConsumed?.();
                return;
            }
            setError(null);
            setFocusedSubtreeIds(null);
            originalPositionsRef.current.clear();
            setLoadStage('building');
            applyRequirementsFromResponseRef.current(initialRequirements);
            setLoadStage(null);
            markRequirementsHydrated(projectId, refreshTrigger);
            onInitialRequirementsConsumed?.();
            return;
        }

        if (cacheKeyReady) {
            return;
        }

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, refreshTrigger, initialRequirements]);

    // Filter nodes when a branch is focused (single-click on a flow node)
    const filteredNodes = useMemo(() => {
        if (!focusedSubtreeIds) {
            return nodes;
        }
        return nodes.filter((node) => focusedSubtreeIds.has(node.id));
    }, [nodes, focusedSubtreeIds]);
    
    // Filter edges to only show edges between visible nodes
    const filteredEdges = useMemo(() => {
        const visibleNodeIds = new Set(filteredNodes.map(n => n.id));
        
        return edges.filter(edge => {
            // Only show edges where both source and target are visible
            return visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target);
        });
    }, [edges, filteredNodes]);

    // Fit the viewport to include all currently rendered nodes, keeping zoom readable
    const fitAllNodes = useCallback(() => {
        if (!reactFlowInstance) return;
        if (!filteredNodes.length) return;

        requestAnimationFrame(() => {
            reactFlowInstance.fitView({
                padding: 0.3,
                includeHiddenNodes: true,
                nodes: filteredNodes,
                minZoom: MIN_READABLE_ZOOM,
                maxZoom: 1,
            });
        });
    }, [reactFlowInstance, filteredNodes]);

    fitAllNodesRef.current = fitAllNodes;

    // Automatically fit when data loads/refetches
    useEffect(() => {
        if (!shouldAutoFit) return;
        if (!reactFlowInstance) return;
        fitAllNodes();
        setShouldAutoFit(false);
    }, [shouldAutoFit, reactFlowInstance, fitAllNodes]);

    // Re-fit viewport when branch focus changes
    useEffect(() => {
        if (!reactFlowInstance || !filteredNodes.length) return;
        requestAnimationFrame(() => {
            reactFlowInstance.fitView({
                padding: 0.3,
                nodes: filteredNodes,
                minZoom: MIN_READABLE_ZOOM,
                maxZoom: 1,
            });
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedSubtreeIds, reactFlowInstance]);

    // Gets the canvas content based on state
    const getCanvasContent = () => {
        if (loading) {
            const stageLabel =
                loadStage === 'building'
                    ? 'Building canvas…'
                    : loadStage === 'fetching'
                      ? 'Fetching project…'
                      : 'Preparing…';
            return (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0A071B]/80 backdrop-blur-sm">
                    <div className="text-center">
                        <div
                            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-purple-500 border-t-transparent"
                            aria-hidden
                        />
                        <p className="mt-4 text-base font-medium text-white/95">Loading project</p>
                        <p className="mt-2 text-sm text-purple-200/90">{stageLabel}</p>
                    </div>
                </div>
            );
        }

        if (error) {
            return <div className="absolute inset-0 flex items-center justify-center bg-[#0A071B]/90 backdrop-blur-sm z-10">
                <div className="text-center max-w-md p-4">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <p className="text-red-300 font-semibold">Error</p>
                    <p className="mt-2 text-gray-300">{error}</p>
                </div>
            </div>;
        }

        if (!projectId) {
            return <div className="absolute inset-0 flex items-center justify-center bg-[#0A071B]/90 backdrop-blur-sm z-10">
                <div className="text-center">
                    <p className="text-gray-300">Select a project to view requirements.</p>
                </div>
            </div>;
        }

        if (nodes.length === 0) {
            return <div className="absolute inset-0 flex items-center justify-center bg-[#0A071B]/90 backdrop-blur-sm z-10">
                <div className="text-center">
                    <p className="text-gray-300">No requirements found for this project.</p>
                </div>
            </div>;
        }

        // Return the React Flow component with just the user flow node
        if (!showCanvas) {
            return <div className="absolute inset-0 flex items-center justify-center bg-[#0A071B]/90 backdrop-blur-sm z-10">
                <div className="text-center">
                    <p className="text-gray-300">Canvas view is not available in this tab.</p>
                </div>
            </div>;
        }

        return (
            <ReactFlow
                onInit={handleFlowInit}
                nodes={filteredNodes.map(node => ({
                    ...node,
                    style: {
                        ...node.style,
                        opacity: selectedNodeId
                            ? (highlightedNodeIds.has(node.id) ? 1 : 0.55)
                            : 1,
                        transition: 'opacity 0.3s ease-in-out, box-shadow 0.35s ease',
                        ...(outlineFlashNodeId === node.id
                            ? {
                                  boxShadow:
                                      '0 0 0 1px rgba(199, 210, 254, 0.95), 0 0 28px rgba(129, 140, 248, 0.55), 0 0 56px rgba(99, 102, 241, 0.22)',
                              }
                            : {}),
                    }
                }))}
                edges={filteredEdges.map(edge => {
                    // Check if this is a hierarchical edge (has strokeDasharray '8,4' or opacity 0.35 in original style)
                    const isHierarchical = edge.id.includes('hierarchical') || 
                                          (edge.style?.strokeDasharray === '8,4') ||
                                          (edge.style?.opacity === 0.35);
                    
                    // Default opacity: main edges = 1, hierarchical edges = dimmed until selection
                    const defaultOpacity = isHierarchical ? 0.55 : 1;
                    
                    return {
                        ...edge,
                        style: {
                            ...edge.style,
                            opacity: selectedNodeId
                                ? (highlightedEdgeIds.has(edge.id) ? 1 : 0.55)
                                : defaultOpacity,
                            strokeWidth: selectedNodeId && highlightedEdgeIds.has(edge.id) ? 3 : (edge.style?.strokeWidth || 2),
                            transition: 'opacity 0.3s ease-in-out, stroke-width 0.3s ease-in-out'
                        },
                        animated: !!(selectedNodeId && highlightedEdgeIds.has(edge.id))
                    };
                })}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onPaneClick={handlePaneClick}
                onNodeClick={handleNodeClickWithFocus}
                onNodeDoubleClick={handleNodeDoubleClickCancelTimer}
                connectionLineType={ConnectionLineType.Bezier}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                attributionPosition="bottom-right"
                snapToGrid={true}
                snapGrid={[15, 15]}
                nodesConnectable={true}
                edgesFocusable={true}
                minZoom={0.2}
                maxZoom={2}
                fitViewOptions={{
                    padding: 0.2,
                    includeHiddenNodes: true
                }}
                defaultEdgeOptions={{
                    // type: 'default' is bezier by default, so we can omit it
                    style: {
                        stroke: '#10b981',
                        strokeWidth: 2,
                        strokeDasharray: '5,5',
                    },
                }}
                connectionLineStyle={{
                    stroke: '#10b981',
                    strokeWidth: 2,
                    strokeDasharray: '5,5',
                }}
            >
                <Controls showFitView={false}>
                    <ControlButton onClick={fitAllNodes} title="Fit entire canvas">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 2H2V14H3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M13 2H14V14H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </ControlButton>
                </Controls>
                <MiniMap
                    position="bottom-right"
                    pannable={true}
                    zoomable={true}
                    nodeColor={(node) => {
                        if (node.type === 'userFlowNode') return '#059669';
                        if (node.type === 'highLevelRequirementNode') return '#2563eb';
                        if (node.type === 'lowLevelRequirementNode') return '#7c3aed';
                        if (node.type === 'filteredRequirementNode') return '#2563eb';
                        return '#6b7280';
                    }}
                    nodeStrokeColor={(node) => {
                        if (node.selected) return '#f59e0b';
                        return '#94a3b8';
                    }}
                    nodeStrokeWidth={2}
                    nodeBorderRadius={6}
                    maskColor="rgba(241, 245, 249, 0.7)"
                    maskStrokeColor="rgba(59, 130, 246, 0.6)"
                    maskStrokeWidth={2}
                    style={{
                        width: 220,
                        height: 150,
                        backgroundColor: '#f8fafc',
                        border: '2px solid #cbd5e1',
                        borderRadius: 12,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    }}
                />
                <Background variant={BackgroundVariant.Dots} gap={12} size={1} color="#6ee7b7" />
            </ReactFlow>
        );
    };


    

    // Handle inline editing
    const startEditing = (fieldName: string, currentValue: string) => {
        setEditingField(fieldName);
        setEditValue(currentValue);
    };
    
    const saveEdit = () => {
        if (editingField && buildSummaryData) {
            if (editingField.startsWith('doc_')) {
                // Handle document links
                const index = parseInt(editingField.split('_')[1]);
                const newLinks = [...buildSummaryData.documentLinks];
                newLinks[index] = editValue;
                setBuildSummaryData(prev => prev ? { ...prev, documentLinks: newLinks } : null);
            } else if (editingField.startsWith('dashboard_')) {
                // Handle dashboard links
                const index = parseInt(editingField.split('_')[1]);
                const newLinks = [...buildSummaryData.dashboardLinks];
                newLinks[index] = editValue;
                setBuildSummaryData(prev => prev ? { ...prev, dashboardLinks: newLinks } : null);
            } else {
                // Handle regular fields
                setBuildSummaryData(prev => prev ? { ...prev, [editingField]: editValue } : null);
            }
        }
        setEditingField(null);
        setEditValue('');
    };
    
    const cancelEdit = () => {
        setEditingField(null);
        setEditValue('');
    };
    
    return (
        <div className="h-full flex flex-col">
            {/* Add CSS for animations */}
            <style>
                {`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                
                /* Keep edges behind nodes */
                .react-flow__edges {
                    z-index: 0 !important;
                }
                
                .react-flow__nodes {
                    z-index: 1 !important;
                }
                
                .react-flow__edge-path {
                    mix-blend-mode: multiply;
                }
                
                /* Panel slide animations */
                .panel-slide-left {
                    transform: translateX(-100%);
                }
                
                .panel-slide-right {
                    transform: translateX(100%);
                }
                
                .panel-slide-bottom {
                    transform: translateY(100%);
                }
                
                /* Make ReactFlow Controls buttons more visible */
                .react-flow__controls-button {
                    background-color: rgba(255, 255, 255, 0.95) !important;
                    border: 1px solid rgba(0, 0, 0, 0.2) !important;
                    color: #1f2937 !important;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
                }
                
                .react-flow__controls-button:hover {
                    background-color: rgba(255, 255, 255, 1) !important;
                    border-color: rgba(0, 0, 0, 0.3) !important;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15) !important;
                }
                
                .react-flow__controls-button svg {
                    fill: #1f2937 !important;
                    stroke: #1f2937 !important;
                }
                
                /* Specifically target reset/interaction mode button */
                .react-flow__controls-button[title*="reset" i],
                .react-flow__controls-button[title*="Reset" i],
                .react-flow__controls-button[aria-label*="reset" i] {
                    background-color: #3b82f6 !important;
                    color: white !important;
                    border-color: #2563eb !important;
                }
                
                .react-flow__controls-button[title*="reset" i]:hover,
                .react-flow__controls-button[title*="Reset" i]:hover,
                .react-flow__controls-button[aria-label*="reset" i]:hover {
                    background-color: #2563eb !important;
                }
                
                .react-flow__controls-button[title*="reset" i] svg,
                .react-flow__controls-button[title*="Reset" i] svg,
                .react-flow__controls-button[aria-label*="reset" i] svg {
                    fill: white !important;
                    stroke: white !important;
                }
                `}
            </style>
            
            {error && (
                <div className="p-4 text-red-300 border-b border-white/10 bg-red-500/10 backdrop-blur-sm">
                    {error}
                </div>
            )}
            
            <div className="flex-1 relative flex min-h-0">
                {/* Main Canvas Area — outline lives in the project sidebar via RequirementsCanvasOutlineProvider */}
                <div className="relative min-h-0 min-w-0 flex-1">
                    {/* ReactFlow component */}
                    {getCanvasContent()}
                </div>
                
            </div>
            
            {/* VishCoder Chat Panel - Same as other tabs */}
            {onToggleRightPanel && (
                <VishCoderChatPanel 
                    isOpen={rightPanelOpen} 
                    onToggle={onToggleRightPanel}
                    page="requirements"
                    projectId={projectId}
                />
            )}
            
            {/* BuildChannelHandler - Handles bottom panel with Live Logs and Progress */}
            {/* Chat is now handled by VishCoderChatPanel component */}
            <BuildChannelHandler
                isBottomPanelOpen={bottomPanelOpen}
                onToggleBottomPanel={toggleBottomPanel}
                buildSummaryData={buildSummaryData}
                setBuildSummaryData={setBuildSummaryData}
                setIsBuildSummaryModalOpen={setIsBuildSummaryModalOpen}
                projectId={projectId}
                onInit={(methods) => {
                    // Store setContractPopups from BuildChannelHandler in ref
                    if (methods.setContractPopups) {
                        setContractPopupsRef.current = methods.setContractPopups;
                    }
                }}
            />
            
            {/* Node Actions Popup - no edit/build actions when read-only */}
            <NodeActionsPopup
                isOpen={nodeActionsPopup.isOpen}
                position={nodeActionsPopup.position}
                actions={canEdit && nodeActionsPopup.node ? buildNodeActions(nodeActionsPopup.node, {
                    onBuildFeature: handleBuildFeature,
                    onBuildEpic: handleBuildEpic,
                    onGenerateChildRequirements: handleGenerateRequirements
                }) : []}
                onClose={() => setNodeActionsPopup({ isOpen: false, position: { x: 0, y: 0 }, node: null })}
            />
            
            
            {/* Add custom styles for the Build Feature menu item */}
            <style>
                {`
                /* react-contexify v6 uses transform (Floating UI) for placement — do not set transform: none */
                .contexify {
                    z-index: 10050 !important;
                }
                
                .contexify_wrapper {
                    z-index: 10050 !important;
                }
                
                .contexify_submenu {
                    z-index: 10050 !important;
                }
                
                .contexify_item {
                    position: relative !important;
                }
                
                .react-flow__container .contexify,
                .react-flow__viewport .contexify,
                .react-flow .contexify {
                    z-index: 10050 !important;
                }
                
                /* Make the text white when the menu item is highlighted */
                .contexify_item:hover .build-feature-text { 
                    color: white !important; 
                }
                
                /* AI Agent log badges */
                .badge {
                    display: inline-block;
                    padding: 2px 6px;
                    border-radius: 4px;
                    font-size: 10px;
                    font-weight: bold;
                    margin-right: 8px;
                }
                
                .log-system {
                    background-color: #6c757d;
                    color: white;
                }
                
                .log-manager {
                    background-color: #007bff;
                    color: white;
                }
                
                .log-coder {
                    background-color: #28a745;
                    color: white;
                }
                
                .log-error {
                    background-color: #dc3545;
                    color: white;
                }
                
                /* Ensure text in agent logs is readable */
                .agent-logs-container {
                    color: #1f2937 !important;
                }
                
                .agent-logs-container .badge {
                    color: white !important;
                }
                `}
            </style>
            
            {/* Flow Detail Modal - edit/build callbacks only when canEdit */}
            <FlowDetailModal
                isOpen={isFlowModalOpen}
                onClose={() => {
                    setIsFlowModalOpen(false);
                    setDeepLinkCommentId(null);
                    setOutlineTestCaseHighlightUiid(null);
                }}
                nodeData={selectedFlowNode}
                highlightCommentId={deepLinkCommentId ?? undefined}
                highlightTestCaseUiid={outlineTestCaseHighlightUiid}
                onSave={canEdit ? handleFlowSave : undefined}
                onBuildFeature={canEdit && selectedFlowNode ? ({ nodeId }) => {
                    handleBuildFeature({ nodeId });
                } : undefined}
                onBuildEpic={canEdit && selectedFlowNode ? ({ nodeId }) => {
                    handleBuildEpic({ nodeId });
                } : undefined}
                onGenerateChildReq={canEdit && selectedFlowNode ? (uiid: string) => {
                    handleGenerateChildReqFromFlowNode(uiid);
                } : undefined}
                onAddChildRequirement={canEdit && selectedFlowNode ? (parentUiid: string, parentNodeType: FlowNodeType) => {
                    handleAddChildRequirement(parentUiid, parentNodeType);
                } : undefined}
                onAddRequirement={canEdit && selectedFlowNode ? (parentUiid: string, nodeType: FlowNodeType) => {
                    handleAddRequirement(parentUiid, nodeType);
                } : undefined}
                onDeleteRequirement={canEdit && selectedFlowNode ? (uiid: string, nodeType: FlowNodeType) => {
                    handleDeleteRequirement(uiid, nodeType);
                } : undefined}
                projectId={projectId ?? undefined}
                allNodesData={nodes}
                onNavigateToChild={handleNavigateToChild}
            />

            {/* Add Requirement Modal */}
            <Modal
                isOpen={isAddRequirementModalOpen}
                onClose={() => {
                    setIsAddRequirementModalOpen(false);
                    setNewRequirementName('');
                    setNewRequirementDescription('');
                    setAddRequirementIntent('sibling');
                }}
                title={
                    addRequirementType
                        ? addRequirementIntent === 'child'
                            ? `Add Child ${requirementTypeLabel(addRequirementType)}`
                            : `Add New ${requirementTypeLabel(addRequirementType)}`
                        : 'Add Requirement'
                }
                size="md"
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Name *
                        </label>
                        <input
                            type="text"
                            value={newRequirementName}
                            onChange={(e) => setNewRequirementName(e.target.value)}
                            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Enter requirement name"
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Description
                        </label>
                        <textarea
                            value={newRequirementDescription}
                            onChange={(e) => setNewRequirementDescription(e.target.value)}
                            rows={4}
                            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                            placeholder="Enter description (optional)"
                        />
                        <DescriptionAttachmentUpload
                            enabled
                            projectId={projectId ?? undefined}
                            onAppend={(fileName, s3Path) =>
                                setNewRequirementDescription((prev) =>
                                    appendAttachmentSuffixToDescription(prev, fileName, s3Path)
                                )
                            }
                        />
                    </div>
                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <button
                            onClick={() => {
                                setIsAddRequirementModalOpen(false);
                                setNewRequirementName('');
                                setNewRequirementDescription('');
                                setAddRequirementIntent('sibling');
                            }}
                            className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCreateRequirement}
                            disabled={!newRequirementName.trim() || loading}
                            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white rounded-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Creating...' : 'Create'}
                        </button>
                    </div>
                </div>
            </Modal>
            
            <BuildSummaryModal
                isOpen={isBuildSummaryModalOpen}
                onClose={() => setIsBuildSummaryModalOpen(false)}
                buildSummaryData={buildSummaryData}
                setBuildSummaryData={setBuildSummaryData}
                editingField={editingField}
                editValue={editValue}
                startEditing={startEditing}
                saveEdit={saveEdit}
                cancelEdit={cancelEdit}
                agentSocket={agentSocket}
            />

            <GenerateChildProgressModal
                isOpen={generateProgressOpen}
                status={generateProgressStatus}
                lines={generateProgressLines}
                contextTitle={generateProgressContextTitle}
                errorMessage={generateProgressError}
                onClose={closeGenerateProgressModal}
            />

            <Toast message={toastMessage} />

        </div>
    );
};

export default CanvasViewer;

